import { maintenanceNotice } from "../i18n/maintenanceMessages";
import { assetTypeDefinition, deriveProjectAssets } from "../utils/projectAssets";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  FormControl,
  InputLabel,
  Switch,
  Select,
  Alert,
  Snackbar,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Drawer,
  Skeleton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { Navigate, Outlet, Link, useLocation, useNavigate  } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SolarPowerOutlinedIcon from "@mui/icons-material/SolarPowerOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import CableOutlinedIcon from "@mui/icons-material/CableOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { useAuth } from "../context/AuthContext";  // <-- use shared auth
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import { useFile } from "../context/FileContext";
import { metenApi } from "../services/metenApi";
import { supabase } from "../lib/supabase";
import { useUi } from "../context/UiContext";
import { useLanguage } from "../context/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useHistory } from "../context/HistoryContext";
import { useDemo } from "../context/DemoContext";
import AccountUsagePanel from "../components/AccountUsagePanel";
import GlossaryDialog from "../components/GlossaryDialog";
import { MANUAL_URLS } from "../utils/manuals";
import ReportProblemDialog from "../components/ReportProblemDialog";
import NewStudyDialog from "../components/NewStudyDialog";
import QuickStudyBanner from "../components/QuickStudyBanner";
import { QUICK_STUDY_PATHS } from "../components/quickStudyRun";
import { DEFAULT_CONFIG } from "../components/config/defaultConfig";
import { applyStudyPreset } from "../components/config/studyPresets";
import { withNewProjectDefaults } from "../components/config/newProjectDefaults";
import AnnouncementsBell from "../components/AnnouncementsBell";
import { normalizeAdminFlag } from "../utils/adminUsers";
import { WeatherGenerationIcon, SunCloudIcon } from "../components/customTabIcons";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SaveAsOutlinedIcon from "@mui/icons-material/SaveAsOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import KeyboardOutlinedIcon from "@mui/icons-material/KeyboardOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined"; // you already have this
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import AutoGraphOutlinedIcon from "@mui/icons-material/AutoGraphOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import StackedBarChartOutlinedIcon from "@mui/icons-material/StackedBarChartOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import RssFeedOutlinedIcon from "@mui/icons-material/RssFeedOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { FEASIBILITY_STEPS, MONITORING_VIEWS, OPERATIONS_AREAS } from "../navigation/sections";
import useFeasibilityAnalyses from "../navigation/useFeasibilityAnalyses";
import useFieldFocus from "../navigation/useFieldFocus";
import useWorkspaceNavigation from "../navigation/useWorkspaceNavigation";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import RedoOutlinedIcon from "@mui/icons-material/RedoOutlined";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  getSupportedMarketsLabel,
  isUnsupportedMarketZone,
  normalizeMarketZone,
} from "../utils/marketSupport";
import { projectErrorMessage } from "../i18n/projectMessages";
import { formatApiError } from "../utils/apiErrors";
import {
  getDemoProjectCreateDisabledMessage,
  getDemoMarketOperationsDisabledMessage,
  getDemoWindSystemDisabledMessage,
  getDemoMarketDataDisabledMessage,
  getRegularFeasibilityDisabledMessage,
  getMonitoringDisabledMessage,
} from "../utils/demoAccess";
import {
  buildProjectExportPayload,
  buildProjectExportFilename,
  buildProjectPackageFilename,
  downloadBlob,
  downloadJson,
  PROJECT_PACKAGE_EXTENSION,
} from "../utils/exportProject";
// The sidebar can be dragged wider, while leaving enough room for the page.
const DRAWER_WIDTH_DEFAULT = 258;
const DRAWER_WIDTH_MIN = 190;
const DRAWER_WIDTH_MAX = 1600;
const DRAWER_WIDTH_KEY = "pythia.sidebarWidth";
const DRAWER_COLLAPSED_KEY = "pythia.sidebarCollapsed";

function clampDrawerWidth(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return DRAWER_WIDTH_DEFAULT;
  return Math.min(DRAWER_WIDTH_MAX, Math.max(DRAWER_WIDTH_MIN, Math.round(n)));
}
const drawerWidthForViewport = (width, viewportWidth) =>
  Math.min(clampDrawerWidth(width), Math.max(DRAWER_WIDTH_MIN, viewportWidth - 280));
const APP_NAME = "PYTHIA";
// No APP_VERSION constant here on purpose. The version is served by the backend
// on /system/status (see api/version.py, the single place it is defined) and
// held in component state below. A build-time constant would be a second number
// to maintain and, worse, could display a version that is not the one running.
// The previous fallback here was "0.1 (beta)" while production served "0.1.0".
// Non-production marker: set REACT_APP_ENV=staging on the staging frontend service.
const IS_STAGING = process.env.REACT_APP_ENV === "staging";
const BRAND_FONT_FAMILY = `"Cinzel", "Playfair Display", "Space Grotesk", "Inter", "Segoe UI", sans-serif`;
// ✅ Use your real website domain (no dash)
const WEBSITE_URL = "https://metensolutions.com";

// Decide where docs live:
// Option 1: website docs page
// The two user manuals ship with the frontend (public/manuals); their URLs,
// version and page map live in utils/manuals.js.
const DOCS_URL = `${WEBSITE_URL}/docs`; // change if your site uses another path
// Option 2 (later): in-app docs route
// const DOCS_URL = "/docs";

const SUPPORT_EMAIL = "anas.chatz@metensolutions.com"; // <-- set your real support email

const MarketDataEuroIcon = () => (
  <Box
    component="span"
    sx={{
      width: 22,
      height: 22,
      borderRadius: "50%",
      border: "2px solid currentColor",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 14,
      fontWeight: 700,
      lineHeight: 1,
      boxSizing: "border-box",
    }}
  >
    €
  </Box>
);


const PRESET_AVATARS = [
  { url: "https://api.dicebear.com/9.x/big-ears/svg?seed=Katherine&background=%23fef3c7" },
  { url: "https://api.dicebear.com/6.x/avataaars/svg?seed=taro&background=%23dbeafe" },
  { url: "https://api.dicebear.com/9.x/avataaars/svg?seed=Robert&background=%23fee2e2" },
  { url: "https://api.dicebear.com/9.x/big-ears/svg?seed=Ryan&background=%23e0f2fe" },
  { url: "https://api.dicebear.com/9.x/big-ears/svg?seed=Jessica&background=%23fefce8" },
  { url: "https://api.dicebear.com/6.x/avataaars/svg?seed=alina&background=%23fef3c7" },
];

const TRANSFORMER_EVENT = "transformerValidationFailed";

function validateTransformerConfig(config) {
  if (!config?.hasTransformer) {
    return { hasMissing: false };
  }

  const details = config.transformerDets || {};
  const loadLoss = details.loadLoss ?? details.load;
  const noLoadLoss = details.noLoadLoss ?? details.noLoad;
  const rated = details.rated ?? details.ratedPower;

  const isInvalidNumber = (value) =>
    value === null ||
    value === undefined ||
    value === "" ||
    !Number.isFinite(Number(value)) ||
    Number(value) <= 0;

  const hasMissing =
    isInvalidNumber(loadLoss) ||
    isInvalidNumber(noLoadLoss) ||
    (!config.useRecommendedTransformer && isInvalidNumber(rated));

  return { hasMissing };
}

function normalizeMustChangePasswordFlag(profile) {
  return Boolean(
    profile?.must_change_password ??
      profile?.mustChangePassword ??
      profile?.force_password_change ??
      profile?.forcePasswordChange ??
      profile?.password_change_required ??
      profile?.passwordChangeRequired
  );
}

function isEditableShortcutTarget(el) {
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  return (
    tag === "input" ||
    tag === "textarea" ||
    tag === "select" ||
    el.isContentEditable ||
    el.getAttribute?.("role") === "combobox"
  );
}

function shouldMarkProjectDirtyFromInput(el) {
  if (!isEditableShortcutTarget(el)) return false;
  if (el.disabled || el.readOnly || el.getAttribute?.("aria-readonly") === "true") return false;
  // [data-dirty-ignore] marks view-only / non-project areas (e.g. Data & Forecasts,
  // optimization run-settings) whose inputs must not flag the project as changed.
  return !el.closest?.('[role="dialog"], [role="menu"], [data-dirty-ignore]');
}

function waitForFocusedInputCommit(el) {
  if (!isEditableShortcutTarget(el)) return Promise.resolve();
  if (typeof el.blur === "function") el.blur();
  return new Promise((resolve) => setTimeout(resolve, 0));
}

export default function AppLayout() {
  const location = useLocation();
  const history = useHistory();
  const navigate = useNavigate();
  const { t, translateText, isGreek } = useLanguage();

  // Sidebar width, dragged from its right edge and remembered across sessions.
  // Read lazily so the first paint is already at the stored width rather than
  // snapping from the default. Wrapped in try/catch because localStorage throws
  // rather than returning null in some privacy modes.
  const [drawerWidth, setDrawerWidth] = useState(() => {
    try {
      const stored = window.localStorage.getItem(DRAWER_WIDTH_KEY);
      return stored === null ? DRAWER_WIDTH_DEFAULT : clampDrawerWidth(stored);
    } catch {
      return DRAWER_WIDTH_DEFAULT;
    }
  });
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(DRAWER_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const visibleDrawerWidth = sidebarCollapsed ? 0 : drawerWidthForViewport(drawerWidth, viewportWidth);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((collapsed) => !collapsed);
  }, []);
  useEffect(() => {
    try {
      window.localStorage.setItem(DRAWER_COLLAPSED_KEY, String(sidebarCollapsed));
    } catch {
      /* storage unavailable; the toggle still works for this session */
    }
  }, [sidebarCollapsed]);

  // Listeners go on window, not the handle: the pointer routinely outruns a 6px
  // strip during a drag, and if it escapes we would stop tracking mid-gesture
  // and never see the mouseup that ends it.
  useEffect(() => {
    if (!isResizingSidebar) return undefined;

    const onMove = (e) => setDrawerWidth(drawerWidthForViewport(e.clientX, window.innerWidth));
    const onUp = (e) => {
      const finalWidth = drawerWidthForViewport(e.clientX, window.innerWidth);
      setDrawerWidth(finalWidth);
      setIsResizingSidebar(false);
      // Persisted once on release rather than on every mousemove, which would
      // be a few hundred writes per drag.
      try {
        window.localStorage.setItem(DRAWER_WIDTH_KEY, String(finalWidth));
      } catch {
        /* storage unavailable; the width still applies for this session */
      }
    };

    // Dragging across the page otherwise selects text and flickers the cursor
    // back to a caret whenever it leaves the handle.
    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
    };
  }, [isResizingSidebar]);

  const resetDrawerWidth = useCallback(() => {
    setDrawerWidth(DRAWER_WIDTH_DEFAULT);
    try {
      window.localStorage.setItem(DRAWER_WIDTH_KEY, String(DRAWER_WIDTH_DEFAULT));
    } catch {
      /* ignore */
    }
  }, []);


  const { user, logout, changePassword, clearPasswordSetupRequirement, mustSetPassword } = useAuth();
  const userId = user?.id || null;
  const file = useFile();
  const latestHistoryRef = useRef(history);
  const latestFileRef = useRef(file);
  const projectJsonInputRef = useRef(null);
  latestHistoryRef.current = history;
  latestFileRef.current = file;
  const { demoProfile, demoLoading, refreshDemoProfile } = useDemo();
  const feasibilityAnalyses = useFeasibilityAnalyses();
  useFieldFocus();           // "#field-…" links land on the field, not just the page
  const [maintenanceStatus, setMaintenanceStatus] = useState({
    maintenance_mode: false,
    maintenance_message: null,
  });
  // The deployed version, reported by the backend on the poll below. This is the
  // ONLY source: there is no build-time constant to keep in step, so the UI can
  // never display a version that is not actually running.
  //
  // Two states are needed, not one. `serverVersion` null means "not answered
  // yet" and shows a skeleton; `versionResolved` records that a poll has
  // completed, so a backend that is unreachable or too old to send the field
  // resolves to "unknown" instead of leaving a skeleton pulsing forever.
  const [serverVersion, setServerVersion] = useState(null);
  const [versionResolved, setVersionResolved] = useState(false);
  const APP_VERSION = serverVersion || (versionResolved ? "unknown" : null);
  // Skeleton rather than a spinner: a spinner implies work in progress and
  // draws the eye to a string that is decorative almost all of the time. This
  // reserves the width instead, so the header does not shift when the real
  // value lands ~200ms later.
  const versionNode = APP_VERSION ?? (
    <Skeleton
      variant="text"
      width={34}
      sx={{ display: "inline-block", verticalAlign: "middle", bgcolor: "rgba(255,255,255,0.25)" }}
    />
  );
  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const status = await metenApi.getSystemStatus();
        if (active) {
          setMaintenanceStatus({
            maintenance_mode: Boolean(status?.maintenance_mode),
            maintenance_message: status?.maintenance_message || null,
          });
          // Only overwrite when the backend actually sent something. An older
          // backend omits the field entirely, and clobbering with undefined
          // would flip the header back to the fallback on every poll.
          const reported = (status?.version || "").trim();
          if (reported) setServerVersion(reported);
        }
      } catch (_) {
        // ignore transient errors; keep last known status
      } finally {
        // Marks the skeleton done whether the call succeeded, failed, or came
        // back from a backend with no `version` field. Without this the
        // placeholder would never resolve on an unreachable API.
        if (active) setVersionResolved(true);
      }
    };
    // Once a minute, and only while the tab is visible. A hidden tab used to
    // keep polling (throttled by the browser to once a minute) all day: about
    // 1,400 requests per forgotten tab, each one a log line. Coming back to the
    // tab checks at once, so a maintenance banner is never a minute late.
    const POLL_MS = 60000;
    let id = null;
    const visible = () =>
      typeof document === "undefined" || document.visibilityState !== "hidden";
    const start = () => {
      if (id != null) return;
      poll();
      id = setInterval(poll, POLL_MS);
    };
    const stop = () => {
      if (id != null) clearInterval(id);
      id = null;
    };
    const onVisibility = () => (visible() ? start() : stop());
    if (visible()) start();
    else poll(); // one read even in a background tab, for the version label
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      active = false;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
  const demoProjectCreateDisabledMessage = useMemo(
    () => {
      const message = getDemoProjectCreateDisabledMessage(demoProfile);
      return message ? projectErrorMessage(message, isGreek) : message;
    },
    [demoProfile, isGreek]
  );
  const demoMarketOperationsDisabledMessage = useMemo(
    () => getDemoMarketOperationsDisabledMessage(demoProfile),
    [demoProfile]
  );
  const demoWindSystemDisabledMessage = useMemo(
    () => getDemoWindSystemDisabledMessage(demoProfile),
    [demoProfile]
  );
  const demoMarketDataDisabledMessage = useMemo(
    () => getDemoMarketDataDisabledMessage(demoProfile),
    [demoProfile]
  );
  const monitoringDisabledMessage = useMemo(
    () => getMonitoringDisabledMessage(demoProfile),
    [demoProfile]
  );

  const {
    darkMode, setDarkMode,
    showAdvanced, setShowAdvanced,
    showTooltips, setShowTooltips,
  } = useUi();

  const [shortcutsDlg, setShortcutsDlg] = useState(false);
  const [reportDlg, setReportDlg] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { workspace, switchWorkspace } = useWorkspaceNavigation();
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success", // success | info | warning | error
    icon: null,
  });
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(Boolean(user));
  const [accountDlg, setAccountDlg] = useState(false);
  const [displayNameInput, setDisplayNameInput] = useState("");
  const [jobRoleInput, setJobRoleInput] = useState("");
  const [avatarPreview, setAvatarPreview] = useState("");
  const [avatarChanged, setAvatarChanged] = useState(false);
  const [avatarFileName, setAvatarFileName] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [passwordState, setPasswordState] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordVisibility, setPasswordVisibility] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const isAdminUsersRoute = location.pathname === "/admin/users";
  useEffect(() => {
    if (!isAdminUsersRoute && location.state?.openAccountProfile) {
      setAccountDlg(true);
    }
  }, [isAdminUsersRoute, location.key, location.state]);
  const dashboardRouteMatches = useCallback(
    (view, marketTab) => {
      if (location.pathname !== "/dashboard") return false;
      const params = new URLSearchParams(location.search);
      if (view) {
        const currentView = params.get("view");
        if ((currentView || "weather") !== view) return false;
      }
      if (marketTab) {
        if (params.get("marketTab") !== marketTab) return false;
      }
      return true;
    },
    [location.pathname, location.search]
  );
  const routeWithQueryMatches = useCallback(
    (target) => {
      const [targetPath, targetQuery = ""] = String(target || "").split("?");
      if (location.pathname !== targetPath) return false;
      if (!targetQuery) return !location.search;
      const currentParams = new URLSearchParams(location.search);
      const targetParams = new URLSearchParams(targetQuery);
      return Array.from(targetParams.entries()).every(
        ([key, value]) => currentParams.get(key) === value
      );
    },
    [location.pathname, location.search]
  );
  const selectedAssetType = assetTypeDefinition(history.present?.systemType);
  const isBessOnlyAsset = Boolean(selectedAssetType?.hasBess && !selectedAssetType.hasPv && !selectedAssetType.hasWind);
  const hideWeatherPage = isBessOnlyAsset && dashboardRouteMatches("weather");
  const dataForecastItems = useMemo(
    () => [
      ...(!isBessOnlyAsset ? [{ label: t("nav.weatherGeneration"), to: "/dashboard?view=weather", icon: <WeatherGenerationIcon /> }] : []),
      { label: t("nav.marketData"), to: "/dashboard?view=market&marketTab=prices", icon: <MarketDataEuroIcon /> },
    ],
    [isBessOnlyAsset, t]
  );
  const projectBiddingZone = normalizeMarketZone(
    history.present?.biddingZone ||
      history.present?.market ||
      history.present?.bidding_zone
  );
  const marketStrategyDisabled = isUnsupportedMarketZone(projectBiddingZone);
  const supportedMarketsLabel = getSupportedMarketsLabel();

  const [presetAnchor, setPresetAnchor] = useState(null);
  const toggleVisibility = (field) => () => {
    setPasswordVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };
  const passwordAdornment = (field) => (
    <InputAdornment position="end">
      <IconButton size="small" onClick={toggleVisibility(field)} edge="end">
        {passwordVisibility[field] ? (
          <VisibilityOff fontSize="small" />
        ) : (
          <Visibility fontSize="small" />
        )}
      </IconButton>
    </InputAdornment>
  );
  const [passwordStatus, setPasswordStatus] = useState({
    loading: false,
    message: "",
    severity: "success",
  });
  const [passwordSetupState, setPasswordSetupState] = useState({
    newPassword: "",
    confirmPassword: "",
    loading: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    let isActive = true;
    if (!userId) {
      setProfile(null);
      setProfileLoading(false);
      return () => {
        isActive = false;
      };
    }

    setProfileLoading(true);
    (async () => {
      try {
        const fetched = await metenApi.getProfile();
        if (!isActive) return;
        setProfile(fetched);
      } catch (err) {
        if (!isActive) return;
        console.error("Failed to load profile:", err);
      } finally {
        if (!isActive) return;
        setProfileLoading(false);
      }
    })();

    return () => {
      isActive = false;
    };
  }, [userId]);

  const fallbackDisplayName =
    user?.user_metadata?.full_name || user?.email || user?.username || "";
  const profileDisplayName = profile?.display_name ?? profile?.displayName ?? "";
  const profileFirstName = profile?.first_name ?? profile?.firstName ?? "";
  const profileLastName = profile?.last_name ?? profile?.lastName ?? "";
  const profileJobRole = profile?.job_role ?? profile?.jobRole ?? "";
  const profileOrganization =
    profile?.organization ?? profile?.org ?? profile?.Organization ?? "";
  const profileAvatarUrl = profile?.avatar_url ?? profile?.avatarUrl ?? "";
  const accountDisplayName = profileDisplayName || fallbackDisplayName || "Unknown user";
  const firstNameTrimmed = profileFirstName?.trim();
  const lastNameTrimmed = profileLastName?.trim();
  const displayNameForTokens = profileDisplayName || fallbackDisplayName || "";
  const nameTokens = displayNameForTokens.split(" ").filter(Boolean);
  const avatarInitials = (() => {
    if (firstNameTrimmed && lastNameTrimmed) {
      return `${firstNameTrimmed[0]}${lastNameTrimmed[0]}`.toUpperCase();
    }
    if (firstNameTrimmed) return firstNameTrimmed[0].toUpperCase();
    if (lastNameTrimmed) return lastNameTrimmed[0].toUpperCase();
    const fallbackName = displayNameForTokens?.trim();
    return fallbackName?.[0]?.toUpperCase() || "A";
  })();
  const accountAvatarSrc =
    avatarChanged && avatarPreview === ""
      ? undefined
      : avatarPreview || profileAvatarUrl || undefined;
  const isDemoUser = Boolean(demoProfile?.demoModeEnabled);
  const isAdminUser = useMemo(() => normalizeAdminFlag(profile, user), [profile, user]);
  const profileRequiresPasswordChange = useMemo(
    () => normalizeMustChangePasswordFlag(profile),
    [profile]
  );
  const effectiveMustSetPassword = mustSetPassword || profileRequiresPasswordChange;
  const isPasswordGatePending = Boolean(user) && profileLoading && !mustSetPassword;
  const accountTextFieldSx = {
    "& .MuiInputBase-input": {
      color: (t) =>
        t.palette.mode === "dark"
          ? "rgba(255,255,255,0.95)"
          : "rgba(15,23,42,0.95)",
    },
    "& .MuiInputLabel-root": {
      color: (t) =>
        t.palette.mode === "dark"
          ? "rgba(255,255,255,0.85)"
          : "rgba(15,23,42,0.75)",
    },
  };
  const brandBlue = "#0B4DA2";
  const readOnlyFieldSx = {
    ...accountTextFieldSx,
    backgroundColor: (t) =>
      t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#f5f7fb",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: (t) =>
          t.palette.mode === "dark"
            ? "rgba(255,255,255,0.25)"
            : "rgba(15,23,42,0.25)",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: (t) =>
          t.palette.mode === "dark"
            ? "rgba(255,255,255,0.25)"
            : "rgba(15,23,42,0.25)",
      },
      boxShadow: "none",
      "&.Mui-focused fieldset": {
        borderColor: (t) =>
          t.palette.mode === "dark"
            ? "rgba(255,255,255,0.25)"
            : "rgba(15,23,42,0.25)",
        borderWidth: "1px",
      },
      "&.Mui-focused": {
        boxShadow: "none",
      },
      cursor: "default",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: (t) =>
        t.palette.mode === "dark"
          ? "rgba(255,255,255,0.25)"
          : "rgba(15,23,42,0.25)",
      borderWidth: "1px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: (t) =>
        t.palette.mode === "dark"
          ? "rgba(255,255,255,0.25)"
          : "rgba(15,23,42,0.25)",
      borderWidth: "1px",
    },
    "& .MuiOutlinedInput-input": {
      cursor: "default",
    },
    "& .MuiInputBase-input": {
      cursor: "default",
    },
    "& .MuiInputBase-root": {
      cursor: "default",
    },
    cursor: "default",
    "& .Mui-disabled": {
      color: (t) =>
        t.palette.mode === "dark" ? "rgba(255,255,255,0.65)" : "rgba(15,23,42,0.65)",
    },
    "& .MuiInputLabel-root": {
      color: (t) => (t.palette.mode === "dark" ? t.palette.text.secondary : brandBlue),
    },
    "& .MuiInputLabel-root.Mui-disabled": {
      color: (t) => (t.palette.mode === "dark" ? t.palette.text.secondary : brandBlue),
    },
  };

  const accountFieldTitleSx = {
    color: (t) => (t.palette.mode === "dark" ? t.palette.text.secondary : brandBlue),
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.03em",
  };

  const accountFieldLabelSx = {
    color: (t) => (t.palette.mode === "dark" ? t.palette.text.secondary : brandBlue),
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: "0.03em",
    "&.Mui-focused": { color: (t) => (t.palette.mode === "dark" ? t.palette.text.primary : brandBlue) },
    "&.MuiInputLabel-shrink": { color: (t) => (t.palette.mode === "dark" ? t.palette.text.secondary : brandBlue) },
    backgroundColor: (t) => t.palette.background.paper,
    px: 0.35,
  };

  const passwordInputSx = {
    "& .MuiInputBase-input": {
      fontSize: 16,
      fontWeight: 540,
      color: (t) =>
        t.palette.mode === "dark"
          ? "rgba(255,255,255,0.8)"
          : "#0f172a",
    },
  };

  const hasProfileChanges = useMemo(() => {
    if (!profile) return false;
    const trimmedDisplayName = (displayNameInput ?? "").trim();
    const trimmedJobRole = (jobRoleInput ?? "").trim();
    const existingDisplayName = profileDisplayName || "";
    const existingJobRole = profileJobRole || "";
    return (
      trimmedDisplayName !== existingDisplayName ||
      trimmedJobRole !== existingJobRole ||
      avatarChanged
    );
  }, [
    profile,
    profileDisplayName,
    profileJobRole,
    displayNameInput,
    jobRoleInput,
    avatarChanged,
  ]);

  useEffect(() => {
    if (!accountDlg) {
      setAvatarPreview("");
      setAvatarChanged(false);
      setAvatarFileName("");
      setPasswordState({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setPasswordStatus({ loading: false, message: "", severity: "success" });
      return;
    }
    setDisplayNameInput(profileDisplayName ?? "");
    setJobRoleInput(profileJobRole ?? "");
    setAvatarPreview(profileAvatarUrl ?? "");
    setAvatarChanged(false);
    setAvatarFileName("");
    setPasswordState({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setPasswordStatus({ loading: false, message: "", severity: "success" });
  }, [accountDlg, profileDisplayName, profileJobRole, profileAvatarUrl]);

  // dialogs
  const [aboutDlg, setAboutDlg] = useState(false);
  const [glossaryDlg, setGlossaryDlg] = useState(false);
  // «Γρήγορη μελέτη», opened by File > New.
  const [newStudyDlg, setNewStudyDlg] = useState(false);
  // The open project closes only here, when the user creates the new one:
  // Cancel in the dialog leaves it open and untouched, so the question about
  // unsaved changes is asked now, not before the dialog.
  const createNewStudy = async (choice, { run = false } = {}) => {
    if (history.isDirty || file.isDirty) {
      const ok = window.confirm(t("file.discard"));
      if (!ok) return;
    }
    if (!choice || choice.kind === "other") {
      setNewStudyDlg(false);
      await file.newFile();
      return;
    }
    // A study chosen from the cards is saved at once. Without a name it is
    // «Χωρίς τίτλο», numbered when that name is taken (names are unique per
    // account). A name the user typed is never changed: if it is taken the
    // dialog stays open and says so.
    const config = applyStudyPreset(withNewProjectDefaults(DEFAULT_CONFIG), { ...choice, isGreek });
    const given = String(choice.name || "").trim();
    const base = given || (isGreek ? "Χωρίς τίτλο" : "Untitled");
    const nameTaken = (e) => e?.status === 409 && /same name|already exists/i.test(String(e?.responseText || e?.message || ""));
    let created = null;
    for (let n = 1; !created; n += 1) {
      const name = n === 1 ? base : `${base} (${n})`;
      try {
        created = await file.createNewProject({ name, config });
      } catch (e) {
        if (!given && nameTaken(e) && n < 50) continue;
        throw new Error(projectErrorMessage(e, isGreek));
      }
    }
    setNewStudyDlg(false);
    showSnack({
      message: t("file.savedAs", { name: created?.name ?? base }),
      severity: "success",
      icon: <SaveAsOutlinedIcon fontSize="small" />,
    });
    // «Δημιουργία και εκτέλεση» goes on by itself once the location is set
    // (components/quickStudyRun.js; the banner above the page leads it).
    navigate(run ? QUICK_STUDY_PATHS.locate : "/project/asset?tab=general");
  };
  const [openDlg, setOpenDlg] = useState(false);
  const [saveAsDlg, setSaveAsDlg] = useState(false);
  // Open dialog state
  const [projects, setProjects] = useState([]);
  // The list arrives after the dialog opens: names only, so it is quick.
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selProjectId, setSelProjectId] = useState("");
  // Save As state
  const [newProjectName, setNewProjectName] = useState("");

  const showSnack = useCallback((opts) => {
    setSnack({
      open: true,
      message: opts.message,
      severity: opts.severity ?? "success",
      icon: opts.icon ?? null,
    });
  }, []);

  const warnProjectCreateBlocked = useCallback(() => {
    if (!demoProjectCreateDisabledMessage) return false;
    showSnack({
      message: demoProjectCreateDisabledMessage,
      severity: "warning",
      icon: <ErrorOutlineOutlinedIcon fontSize="small" />,
    });
    return true;
  }, [demoProjectCreateDisabledMessage, showSnack]);

  const prettyError = useCallback((e) => {
    const data = e?.response?.data;
    if (data?.detail?.message) return data.detail.message;
    if (data?.detail) return data.detail;
    if (typeof e === "object" && e?.detail?.message) return e.detail.message;
    if (typeof e === "object" && e?.detail) return e.detail;

    const msg = e?.message || "";
    try {
      const parsed = JSON.parse(msg);
      if (parsed?.detail?.message) return parsed.detail.message;
      if (parsed?.detail) return parsed.detail;
      if (parsed?.message) return parsed.message;
    } catch {}
    return msg || "Something went wrong";
  }, []);

  const prettifyDetail = useCallback((detail) => {
    const detailCode =
      typeof detail === "object" && detail
        ? detail?.code || detail?.detail?.code
        : undefined;
    const normalizedDetail =
      typeof detail === "string"
        ? detail
        : detail?.message || detail?.detail?.message || detail?.detail || detail?.error || "";
    const d = String(normalizedDetail || "").toLowerCase();
    if (d.includes("same name") || d.includes("already exists")) {
      return "A project with this name already exists. Please choose a different name.";
    }
    if (detailCode === "demo_expired") {
      return "Your demo period has expired.";
    }
    if (detailCode === "demo_project_limit_reached") {
      return "Demo project limit reached.";
    }
    if (detailCode === "daily_trigger_limit_reached") {
      return "You have reached today's demo run limit.";
    }
    if (detailCode === "demo_misconfigured") {
      return "Demo account is locked.";
    }
    if (d.includes("demo period has expired")) {
      return "Your demo period has expired.";
    }
    if (d.includes("demo account is locked")) {
      return "Demo account is locked.";
    }
    if (d.includes("daily demo trigger limit reached")) {
      return "You have reached today's demo run limit.";
    }
    if (d.includes("demo project limit") || d.includes("project limit reached")) {
      return "Demo project limit reached.";
    }
    if (
      demoProfile?.demoLocked &&
      (d.includes("failed to fetch") ||
        d.includes("networkerror") ||
        d.includes("network error") ||
        d.includes("load failed"))
    ) {
      return "Demo account is locked.";
    }
    if (d.includes("account is locked")) {
      return "Account is locked.";
    }
    if (d === "not found" || d.includes("404")) {
      return "The user management endpoint is not available on the backend. Expected GET /admin/users.";
    }
    return formatApiError(detail, normalizedDetail || "Something went wrong");
  }, [demoProfile?.demoLocked]);

  const handleProjectPackageFileChange = useCallback(async (event) => {
    const input = event.target;
    const selectedFile = input?.files?.[0];
    if (!selectedFile) return;

    try {
      if (history.isDirty || file.isDirty) {
        const ok = window.confirm(t("file.discardImport"));
        if (!ok) return;
      }

      const lowerName = selectedFile.name.toLowerCase();
      let payload;

      if (lowerName.endsWith(PROJECT_PACKAGE_EXTENSION)) {
        // Encrypted PYTHIA package: decrypted server-side (auth required).
        payload = await metenApi.importProjectPackage(selectedFile);
      } else if (isAdminUser) {
        // Raw JSON import is kept for administrators only (debug/support).
        // Judged by content, not extension: Windows sometimes hands the file
        // over under its DOS 8.3 short name (e.g. "PPA_CH~1.JSO" for
        // "PPA_check_01_....json"), so an .endsWith(".json") check would
        // wrongly reject valid exports.
        const text = await selectedFile.text();
        try {
          payload = JSON.parse(text);
        } catch {
          throw new Error(
            lowerName.endsWith(".json")
              ? "Invalid project JSON file. The selected file is not valid JSON."
              : `Invalid project file. Please select a ${PROJECT_PACKAGE_EXTENSION} file exported by PYTHIA (or, as an administrator, a raw JSON export).`
          );
        }
      } else {
        throw new Error(
          `Invalid project file. Please select a ${PROJECT_PACKAGE_EXTENSION} file exported by PYTHIA.`
        );
      }

      const importedProject = file.importProjectJson(payload);
      showSnack({
        message: t("file.imported", { name: importedProject.name }),
        severity: "success",
        icon: <UploadFileOutlinedIcon fontSize="small" />,
      });
    } catch (e) {
      showSnack({
        message: projectErrorMessage(e, isGreek),
        severity: "error",
        icon: <ErrorOutlineOutlinedIcon fontSize="small" />,
      });
    } finally {
      input.value = "";
    }
  }, [file, history.isDirty, isAdminUser, isGreek, t, showSnack]);

  const handleAvatarSelect = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setAvatarPreview(reader.result);
      setAvatarChanged(true);
      setAvatarFileName(file.name);
    };
    reader.onerror = () => {
      showSnack({
        message: "Unable to read the selected file",
        severity: "error",
        icon: <ErrorOutlineOutlinedIcon fontSize="small" />,
      });
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const handleClearAvatar = () => {
    setAvatarPreview("");
    setAvatarChanged(true);
    setAvatarFileName("");
  };

  const openPresetMenu = (event) => setPresetAnchor(event.currentTarget);
  const closePresetMenu = () => setPresetAnchor(null);
  const handlePresetSelect = (preset) => {
    setAvatarPreview(preset.url);
    setAvatarChanged(true);
    setAvatarFileName(preset.label);
    closePresetMenu();
  };

  const handleProfileSave = useCallback(async () => {
    if (!profile) {
      showSnack({
        message: "Profile is not loaded yet",
        severity: "warning",
      });
      return;
    }

    const updates = {};
    const trimmedDisplayName = displayNameInput.trim();
    if (trimmedDisplayName !== (profile.display_name ?? "")) {
      updates.display_name = trimmedDisplayName || null;
    }
    const trimmedJobRole = jobRoleInput.trim();
    if (trimmedJobRole !== (profile.job_role ?? "")) {
      updates.job_role = trimmedJobRole || null;
    }
    if (avatarChanged) {
      updates.avatar_url = avatarPreview || null;
    }

    if (!Object.keys(updates).length) {
      showSnack({
        message: "No changes to save",
        severity: "info",
      });
      return;
    }

    setProfileSaving(true);
    try {
      const updatedProfile = await metenApi.updateProfile(updates);
      setProfile(updatedProfile);
      setJobRoleInput(updatedProfile.job_role ?? "");
      setAvatarChanged(false);
      setAvatarFileName("");
      setDisplayNameInput(updatedProfile.display_name ?? "");
      showSnack({
        message: "Profile updated",
        severity: "success",
        icon: <CheckCircleOutlineIcon fontSize="small" />,
      });
    } catch (err) {
      showSnack({
        message: prettifyDetail(prettyError(err)),
        severity: "error",
        icon: <ErrorOutlineOutlinedIcon fontSize="small" />,
      });
    } finally {
      setProfileSaving(false);
    }
  }, [
    avatarChanged,
    avatarPreview,
    displayNameInput,
    jobRoleInput,
    profile,
    prettifyDetail,
    prettyError,
    showSnack,
  ]);

  const handlePasswordChange = useCallback(async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordState;
    if (!currentPassword) {
      setPasswordStatus({
        loading: false,
        message: "Enter your current password",
        severity: "error",
      });
      return;
    }
    if (!newPassword || !confirmPassword) {
      setPasswordStatus({
        loading: false,
        message: "Please complete both new password fields",
        severity: "error",
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordStatus({
        loading: false,
        message: "Passwords do not match",
        severity: "error",
      });
      return;
    }

    const email = user?.email;
    if (!email) {
      setPasswordStatus({
        loading: false,
        message: "Unable to verify your account email",
        severity: "error",
      });
      return;
    }

    setPasswordStatus({ loading: true, message: "", severity: "success" });
    try {
      const { error: verifyError } = await supabase.auth.signInWithPassword(
        { email, password: currentPassword },
        { shouldCreateSession: false }
      );
      if (verifyError) throw verifyError;

      await changePassword({ password: newPassword });
      setPasswordStatus({
        loading: false,
        message: "Password updated",
        severity: "success",
      });
      setPasswordState({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      setPasswordStatus({
        loading: false,
        message: prettyError(err),
        severity: "error",
      });
    }
  }, [changePassword, passwordState, prettyError, user?.email]);
  const passwordMismatch =
    passwordState.newPassword &&
    passwordState.confirmPassword &&
    passwordState.newPassword !== passwordState.confirmPassword;
  const passwordSetupMismatch =
    passwordSetupState.newPassword &&
    passwordSetupState.confirmPassword &&
    passwordSetupState.newPassword !== passwordSetupState.confirmPassword;
  const isPasswordFormValid =
    Boolean(passwordState.currentPassword) &&
    Boolean(passwordState.newPassword) &&
    Boolean(passwordState.confirmPassword) &&
    !passwordMismatch;
  const isPasswordSetupValid =
    Boolean(passwordSetupState.newPassword) &&
    Boolean(passwordSetupState.confirmPassword) &&
    !passwordSetupMismatch;

  const handleInitialPasswordSetup = useCallback(async () => {
    if (!passwordSetupState.newPassword || !passwordSetupState.confirmPassword) {
      setPasswordSetupState((prev) => ({
        ...prev,
        message: "Please complete both password fields",
        severity: "error",
      }));
      return;
    }

    if (passwordSetupState.newPassword !== passwordSetupState.confirmPassword) {
      setPasswordSetupState((prev) => ({
        ...prev,
        message: "Passwords do not match",
        severity: "error",
      }));
      return;
    }

    setPasswordSetupState((prev) => ({
      ...prev,
      loading: true,
      message: "",
    }));

    try {
      await changePassword({ password: passwordSetupState.newPassword });
      let completionPayload;
      try {
        completionPayload = await metenApi.completePasswordChange();
      } catch (completionErr) {
        const completionMessage = prettyError(completionErr);
        const lowerMessage = String(completionMessage || "").toLowerCase();
        let message = completionMessage || "Backend confirmation failed.";

        if (lowerMessage.includes("409")) {
          message = completionMessage;
        } else if (lowerMessage.includes("401") || lowerMessage.includes("403")) {
          message = "Authentication issue while confirming password change. Please sign in again.";
        } else if (
          lowerMessage.includes("networkerror") ||
          lowerMessage.includes("network error") ||
          lowerMessage.includes("failed to fetch") ||
          lowerMessage.includes("load failed")
        ) {
          message =
            "Password updated, but backend confirmation could not be reached. You are still blocked until confirmation succeeds.";
        } else if (!message) {
          message =
            "Password updated, but backend confirmation failed. You are still blocked until confirmation succeeds.";
        }

        throw new Error(message);
      }
      clearPasswordSetupRequirement();
      if (completionPayload && typeof completionPayload === "object") {
        setProfile((prev) => ({ ...(prev || {}), ...completionPayload, must_change_password: false }));
      } else {
        setProfile((prev) => (prev ? { ...prev, must_change_password: false } : prev));
      }
      setPasswordSetupState({
        newPassword: "",
        confirmPassword: "",
        loading: false,
        message: "",
        severity: "success",
      });
      navigate("/Project", { replace: true });
      showSnack({
        message: "Password created. You can now use the app normally.",
        severity: "success",
        icon: <CheckCircleOutlineIcon fontSize="small" />,
      });
    } catch (err) {
      setPasswordSetupState((prev) => ({
        ...prev,
        loading: false,
        message: prettyError(err),
        severity: "error",
      }));
    }
  }, [
    changePassword,
    clearPasswordSetupRequirement,
    navigate,
    passwordSetupState.confirmPassword,
    passwordSetupState.newPassword,
    prettyError,
    showSnack,
  ]);

  useEffect(() => {
    if (!effectiveMustSetPassword) {
      setPasswordSetupState({
        newPassword: "",
        confirmPassword: "",
        loading: false,
        message: "",
        severity: "info",
      });
      return;
    }

    setAccountDlg(false);
    setPasswordSetupState((prev) => ({
      ...prev,
      loading: false,
      message: "",
      severity: "info",
    }));
  }, [effectiveMustSetPassword]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!history.isDirty && !file.isDirty) return;

      e.preventDefault();
      // Chrome requires returnValue to be set
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [file.isDirty, history.isDirty]);

  useEffect(() => {
    const onProjectInputChange = (event) => {
      const activeFile = latestFileRef.current;
      if (!activeFile?.currentProject?.id) return;
      if (!shouldMarkProjectDirtyFromInput(event.target)) return;
      activeFile.setIsDirty?.(true);
    };

    window.addEventListener("input", onProjectInputChange, true);
    window.addEventListener("change", onProjectInputChange, true);
    return () => {
      window.removeEventListener("input", onProjectInputChange, true);
      window.removeEventListener("change", onProjectInputChange, true);
    };
  }, []);



  useEffect(() => {
    const onKeyDown = async (e) => {
      const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.platform);
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (!mod) return;

      // Layout-independent shortcut detection: e.key follows the ACTIVE
      // keyboard layout (Greek: s -> "σ", z -> "ζ", o -> "ο"), which used to
      // break every Ctrl shortcut on non-Latin layouts and let the browser's
      // native action fire (e.g. Ctrl+S -> "Save Page As"). e.code is the
      // PHYSICAL key (e.g. "KeyS") and works on any layout.
      const code = e.code || "";
      const key = code.startsWith("Key")
        ? code.slice(3).toLowerCase()
        : (e.key || "").toLowerCase();

      // avoid stealing shortcuts while typing
      const el = e.target;
      const typing = isEditableShortcutTarget(el);

      if (typing && key !== "s") return;

      // Undo / Redo
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        history.undo();
        return;
      }
      if (key === "z" && e.shiftKey) {
        e.preventDefault();
        history.redo();
        return;
      }
      if (key === "y") {
        e.preventDefault();
        history.redo();
        return;
      }

      // ✅ Open: Ctrl/Cmd + O
      if (key === "o") {
        e.preventDefault();
        e.stopPropagation();

        // if dirty -> confirm
        if (history.isDirty || file.isDirty) {
          const ok = window.confirm(t("file.discard"));
          if (!ok) return;
        }

        setProjects([]);
        setSelProjectId("");
        setProjectsLoading(true);
        setOpenDlg(true);
        try {
          const ps = await metenApi.listProjectSummaries();
          setProjects(ps);
        } catch (err) {
          setOpenDlg(false);
          showSnack({
            message: projectErrorMessage(err, isGreek),
            severity: "error",
            icon: <ErrorOutlineOutlinedIcon fontSize="small" />,
          });
        } finally {
          setProjectsLoading(false);
        }
        return;
      }

      // ✅ Save As: Ctrl/Cmd + Shift + S
      if (key === "s" && e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        if (latestFileRef.current.isSaving) return;
        if (typing) await waitForFocusedInputCommit(el);
        if (warnProjectCreateBlocked()) return;
        setNewProjectName(file.currentProject?.name || "");
        setSaveAsDlg(true);
        return;
      }

      // ✅ Save: Ctrl/Cmd + S
      if (key === "s" && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        if (latestFileRef.current.isSaving) return;

        if (typing) await waitForFocusedInputCommit(el);

        const activeHistory = latestHistoryRef.current;
        const activeFile = latestFileRef.current;
        const snapshot = activeHistory.present ?? {};
        const { hasMissing } = validateTransformerConfig(snapshot);
        if (hasMissing) {
          try {
            window.dispatchEvent(new Event(TRANSFORMER_EVENT));
          } catch {}
          return;
        }

        // if no project yet -> behave like Save As
        if (!activeFile.currentProject?.id) {
          if (warnProjectCreateBlocked()) return;
          setNewProjectName(activeFile.currentProject?.name || "");
          setSaveAsDlg(true);
          return;
        }

        setSaving(true);
        try {
          await activeFile.save();
          showSnack({
            message: t("file.saved"),
            severity: "success",
            icon: <SaveOutlinedIcon fontSize="small" />,
          });
        } catch (err) {
          showSnack({
            message: projectErrorMessage(err, isGreek),
            severity: "error",
            icon: <ErrorOutlineOutlinedIcon fontSize="small" />,
          });
        } finally {
          setSaving(false);
        }
      }
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [
      history,
      file,
      showSnack,
      prettyError,
      prettifyDetail,
      warnProjectCreateBlocked,
      isGreek,
      t,
  ]);


  const readSystemType = () => {
    try {
      const raw = localStorage.getItem("pvConfig_full");
      if (!raw) return "Hybrid";
      const parsed = JSON.parse(raw);
      return parsed?.systemType || "Hybrid";
    } catch {
      return "Hybrid";
    }
  };

  const readWindEnabled = () => {
    try {
      const raw = localStorage.getItem("pvConfig_full");
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      return deriveProjectAssets(parsed).hasWind;
    } catch {
      return false;
    }
  };

  const [systemType, setSystemType] = useState(readSystemType);
  const [windEnabled, setWindEnabled] = useState(readWindEnabled);

  useEffect(() => {
    const onCfgChanged = () => {
      setSystemType(readSystemType());
      setWindEnabled(readWindEnabled());
    };
    window.addEventListener("pvConfigChanged", onCfgChanged);
    window.addEventListener("storage", onCfgChanged); // if another tab changes
    return () => {
      window.removeEventListener("pvConfigChanged", onCfgChanged);
      window.removeEventListener("storage", onCfgChanged);
    };
  }, []);


  useEffect(() => {
    if (
      !deriveProjectAssets({ systemType }).hasPv &&
      location.pathname === "/Photovoltaic"
    ) {
      navigate("/project/asset?tab=general", { replace: true });
    }
  }, [systemType, location.pathname, navigate]);

  useEffect(() => {
    if (!windEnabled && location.pathname === "/Wind") {
      navigate("/Project", { replace: true });
    }
  }, [windEnabled, location.pathname, navigate]);









  const closeSnack = (_, reason) => {
    if (reason === "clickaway") return;
    setSnack((s) => ({ ...s, open: false }));
  };


  const handleDeleteProjectFromOpen = async (projectId) => {
    const deletingSelected = selProjectId === projectId;

    setSaving(true);
    try {
      await metenApi.deleteProject(projectId);

      const ps = await metenApi.listProjectSummaries();
      setProjects(ps);
      // The project count in the top bar drops by one.
      void refreshDemoProfile({ quiet: true });

      if (deletingSelected) setSelProjectId("");

      // if you deleted the currently opened file, close it
      if (file.currentProject?.id === projectId) {
        await file.newFile();
      }

      showSnack({
        message: t("file.deleted"),
        severity: "success",
        icon: <CheckCircleOutlineIcon fontSize="small" />,
      });
    } catch (e) {
      showSnack({
        message: projectErrorMessage(e, isGreek),
        severity: "error",
        icon: <ErrorOutlineOutlinedIcon fontSize="small" />,
      });
    } finally {
      setSaving(false);
    }
  };


  // account menu
  const [accountEl, setAccountEl] = useState(null);

  // top menus
  const [fileEl, setFileEl] = useState(null);
  const [editEl, setEditEl] = useState(null);
  const [viewEl, setViewEl] = useState(null);
  const [helpEl, setHelpEl] = useState(null);





  const title = `${file.currentProject?.name || t("file.untitled")}${file.isDirty ? " *" : ""}`;

  const menuPaperSx = {
    borderRadius: 2,
    mt: 1,
    minWidth: 260,
    overflow: "hidden",
    boxShadow: 10,
    border: (t) => `1px solid ${t.palette.mode === "dark" ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.08)"}`,
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    backgroundColor: (t) =>
      t.palette.mode === "dark"
        ? "rgba(18,26,22,0.92)"
        : "rgba(255,255,255,0.92)",
  };

  const menuItemSx = {
    py: 1.1,
    px: 1.5,
    gap: 1.25,
    "&:hover": { backgroundColor: "action.hover" },
  };

  const menuIconSx = {
    minWidth: 34,
    color: "text.secondary",
  };

  const topMenuBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    color: (t) => (t.palette.mode === "dark" ? "#f8fafc" : "#0f172a"),
    fontFamily: `"Inter", "Segoe UI", sans-serif`,
    letterSpacing: "0.04em",
    fontSize: 15,
    px: 0.8,
    py: 0,
    borderRadius: 1,
    border: "1.5px solid transparent",
    backgroundColor: "transparent",
    minWidth: 60,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    boxShadow: "none",
    "&:hover": {
      borderColor: (t) =>
        t.palette.mode === "dark" ? "rgba(255,255,255,0.25)" : "rgba(15,23,42,0.25)",
      backgroundColor: "action.hover",
      color: "text.primary",
    },
  };
  const projectBadgeSx = {
    ml: 1.5,
    px: 1.4,
    py: 0.45,
    minWidth: 200,
    minHeight: 44,
    borderRadius: 999,
    backgroundColor: "transparent",
    border: "none",
    display: "flex",
    alignItems: "center",
    // The label and the project name are two flex items, so a space typed
    // between them is dropped: "Έργο:Virtual Net Billing sizing".
    gap: 0.75,
    position: "relative",
    "&::before": {
      content: "''",
      position: "absolute",
      left: "-5px",
      top: "5%",
      bottom: "5%",
      width: 4,
      borderRadius: 999,
      backgroundImage:
        "linear-gradient(180deg, rgb(3, 39, 117) 0%, rgb(59, 119, 147) 100%)",
      boxShadow: "0 0 12px rgba(37,99,235,0.5)",
    },
  };

  // Slimmer rows: the sidebar has 4 sections and 12 destinations, and at the
  // previous 44px/40px heights the Feasibility Study group fell below the fold
  // on a 900px-tall window.
  //
  // Height and margin only — the 15px label size is unchanged and should stay
  // that way. Shrinking the type was tried and reverted: at 15px with
  // lineHeight 1.3 the text box is ~19.5px, which fits a 30px row with room to
  // spare, so smaller type buys no vertical space and only costs legibility.
  // Three states, each a clear step apart: at rest no box at all, hover a
  // faint wash, and the open page a tinted row with a bar in the platform's
  // dark blue down its left edge - the one mark hover never gets, so with the
  // pointer resting on another row it is still obvious which page is open.
  // No border and squarer corners, so eleven rows do not read as eleven
  // buttons; and no bold on the open page, so its label does not widen.
  const navItemSx = {
    minHeight: 34,
    borderRadius: "4px",
    mx: 1,
    my: 0.3,
    py: 0.15,

    // The bar's width at rest too, so the text never shifts.
    borderLeft: "3px solid transparent",
    color: (t) => t.palette.mode === "dark" ? t.palette.text.secondary : "#36516b",

    "& .MuiListItemIcon-root": {
      color: (t) => t.palette.mode === "dark" ? t.palette.text.secondary : "#5c7792",
    },

    "&:hover": {
      backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(48,76,103,0.07)",
      color: (t) => t.palette.mode === "dark" ? t.palette.text.primary : "#244563",
    },

    "&.Mui-selected": {
      // Strong enough to be the one focal point of the list: the faint tint
      // it had was barely apart from hover on a white panel.
      backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(76,175,132,0.2)" : "#d6e6f7",
      color: (t) => t.palette.mode === "dark" ? t.palette.text.primary : "#0b3b6e",
      borderLeftColor: (t) => t.palette.mode === "dark" ? t.palette.primary.main : "#0b4da2",
      "& .MuiListItemText-primary": { fontWeight: 600 },
      "& .MuiListItemIcon-root": { color: (t) => t.palette.mode === "dark" ? t.palette.primary.main : "#0b4da2" },

      "&:hover": {
        backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(76,175,132,0.26)" : "#cadff3",
      },
    },
  };
  const navSubItemSx = {
    ...navItemSx,
    minHeight: 30,
    mx: 2,
    my: 0.2,
    py: 0.1,
  };

  // ---------------------------------------------------------------------------
  // Sidebar. The project is the hub and stays on top; below it the user picks
  // a WORKSPACE - Feasibility (one asset at a time: its setup, its data, its
  // study) or Operations (the whole portfolio in the markets) - and the list
  // shows its workflow alongside shared data pages. Shared pages retain the selected workspace.
  // ---------------------------------------------------------------------------
  const isNavItemSelected = (to) => {
    if (to === "/dashboard?view=weather") return dashboardRouteMatches("weather");
    if (to.startsWith("/dashboard?view=market")) return dashboardRouteMatches("market");
    if (to === "/monitoring") return location.pathname === to || location.pathname.startsWith("/monitoring/parks/");
    if (to.startsWith("/ops") || to.startsWith("/project/") || to.startsWith("/monitoring/")) return location.pathname === to || location.pathname.startsWith(`${to}/`);
    if (to === "/optimize") return location.pathname === to;
    if (to === "/dashboard") return location.pathname === "/dashboard" && !new URLSearchParams(location.search).get("view");
    if (to.includes("?")) return routeWithQueryMatches(to);
    return location.pathname === to;
  };
  const navItemDisabledReason = (to) => {
    if (to === "/Wind" && demoWindSystemDisabledMessage) return true;
    if ((to.includes("view=market") || to.includes("view=weather")) && demoMarketDataDisabledMessage) return true;
    if (to.startsWith("/feasibility") && getRegularFeasibilityDisabledMessage(demoProfile)) return true;
    return false;
  };
  const operationsGated = marketStrategyDisabled || Boolean(demoMarketOperationsDisabledMessage);

  // `disabledReason`: an item that does not apply to the current project stays
  // visible but inert, and the reason is shown on hover (an analysis that is
  // only for some project types, say).
  const renderNavItem = ({ label, to, icon, gated = false, disabledReason = "" }) => {
    const itemDisabled = navItemDisabledReason(to) || (gated && operationsGated) || Boolean(disabledReason);
    const item = (
      <ListItemButton
        key={to}
        component={itemDisabled ? "div" : Link}
        to={itemDisabled ? undefined : to}
        selected={isNavItemSelected(to)}
        disabled={itemDisabled}
        onClick={() => setAccountDlg(false)}
        sx={navSubItemSx}
      >
        <ListItemIcon sx={{ minWidth: 26 }}>{icon}</ListItemIcon>
        <ListItemText primary={translateText(label)} primaryTypographyProps={{ fontSize: 15, fontWeight: 500, lineHeight: 1.3 }} />
      </ListItemButton>
    );
    if (!disabledReason) return item;
    return (
      <Tooltip key={to} title={translateText(disabledReason)} placement="right" arrow>
        <span style={{ display: "block" }}>{item}</span>
      </Tooltip>
    );
  };
  // `tight` is for the caption that comes straight after the Project divider:
  // the divider already provides the separation, so the usual top padding
  // would push the first section down for no reason.
  const renderNavCaption = (text, key, { tight = false } = {}) => (
    <Typography
      key={`caption-${key || text}`}
      variant="caption"
      sx={{ display: "block", px: 2.25, pt: tight ? 0.5 : 1.25, pb: 0.25, color: "text.secondary", fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", fontSize: 11.5 }}
    >
      {translateText(text)}
    </Typography>
  );

  const gatedFooter = (
    <>
      {marketStrategyDisabled && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" sx={{ display: "block", color: "warning.main", fontWeight: 700 }}>
            Bidding zone {projectBiddingZone} is not supported for Market Operations.
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: 0.25 }}>
            Supported zones: {supportedMarketsLabel}
          </Typography>
        </Box>
      )}
      {demoMarketOperationsDisabledMessage && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" sx={{ display: "block", color: "warning.main", fontWeight: 700 }}>
            {demoMarketOperationsDisabledMessage}
          </Typography>
        </Box>
      )}
    </>
  );

  const STEP_ICONS = {
    general: <BadgeOutlinedIcon />,
    weather: <SunCloudIcon />,
    technical: <EngineeringOutlinedIcon />,
    economics: <MonetizationOnOutlinedIcon />,
    pvSizing: <TuneOutlinedIcon />,
    simulation: <AutoGraphOutlinedIcon />,
  };
  const OPS_ICONS = {
    desk: <SpeedOutlinedIcon />,
    positions: <StackedBarChartOutlinedIcon />,
    orders: <ReceiptLongOutlinedIcon />,
    reserves: <BoltOutlinedIcon />,
    passes: <TimelineOutlinedIcon />,
    optimize: <PlayArrowIcon />,
    runs: <HistoryOutlinedIcon />,
    bids: <CalendarMonthOutlinedIcon />,
    settlement: <AssessmentOutlinedIcon />,
    inputs: <RssFeedOutlinedIcon />,
    assets: <BatteryChargingFullIcon />,
    settings: <SettingsOutlinedIcon />,
  };

  const feasibilityWorkspace = (
    <>
      {renderNavCaption("Feasibility Study", "feasibility", { tight: true })}
      {FEASIBILITY_STEPS.map((s) => renderNavItem({ label: s.label, to: s.to, icon: STEP_ICONS[s.key] }))}
      {feasibilityAnalyses.length > 0 && <>
        <Divider sx={{ mx: 1, mt: 0.75, mb: 0 }} />
        {renderNavCaption("Tools", "tools", { tight: true })}
        {/* Always open: the page itself says when the project does not qualify. */}
        {feasibilityAnalyses.map((a) => renderNavItem({ label: a.label, to: a.to, icon: STEP_ICONS[a.key] }))}
      </>}
      <Divider sx={{ mx: 1, mt: 0.75, mb: 0 }} />
      {renderNavCaption("Data & Forecasts", "data", { tight: true })}
      {dataForecastItems.map(renderNavItem)}
    </>
  );
  const MONITORING_ICONS = {
    portfolio: <SolarPowerOutlinedIcon />,
    alarms: <NotificationsActiveOutlinedIcon />,
    reports: <SummarizeOutlinedIcon />,
    sources: <CableOutlinedIcon />,
  };
  const operationsWorkspace = (
    <>
      {renderNavCaption("Data & Forecasts", "data-ops", { tight: true })}
      {dataForecastItems.map(renderNavItem)}
      {!monitoringDisabledMessage && <>
        {renderNavCaption("Asset Monitoring", "monitoring")}
        {MONITORING_VIEWS.map((v) => renderNavItem({ label: v.label, to: v.to, icon: MONITORING_ICONS[v.key] }))}
      </>}
      {OPERATIONS_AREAS.map((area) => (
        <React.Fragment key={area.key}>
          {renderNavCaption(area.key === "desk" ? "Market Operations" : area.label, area.key)}
          {area.views.map((v) => renderNavItem({ label: v.label, to: v.to, icon: OPS_ICONS[v.key], gated: true }))}
        </React.Fragment>
      ))}
      {operationsGated && gatedFooter}
    </>
  );

  // Workspace switch lives in the top bar with the other context (project,
  // account): choosing a workspace changes what the sidebar contains.
  const workspaceSwitcher = (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={workspace}
      onChange={(_, v) => v && switchWorkspace(v)}
      sx={{
        ml: 2,
        bgcolor: (t) => (t.palette.mode === "dark" ? "rgba(15,23,42,0.35)" : "rgba(255,255,255,0.45)"),
        border: "1px solid",
        borderColor: (t) => (t.palette.mode === "dark" ? "rgba(226,232,240,0.22)" : "rgba(15,23,42,0.16)"),
        borderRadius: "6px",
        p: 0.25,
        "& .MuiToggleButton-root": {
          textTransform: "none",
          fontWeight: 700,
          fontSize: 13,
          px: 1.5,
          py: 0.4,
          border: "none",
          borderRadius: "4px !important",
          gap: 0.6,
          color: (t) => (t.palette.mode === "dark" ? "#e2e8f0" : "#1e293b"),
        },
        "& .MuiToggleButton-root.Mui-selected": {
          bgcolor: "primary.main",
          color: "primary.contrastText",
          boxShadow: "0 1px 3px rgba(15,23,42,0.16)",
          "&:hover": { bgcolor: "primary.dark" },
        },
      }}
    >
      <ToggleButton value="feasibility" aria-label={t("nav.feasibilityStudy")}
        disabled={Boolean(getRegularFeasibilityDisabledMessage(demoProfile))}>
        <AssessmentOutlinedIcon sx={{ fontSize: 17 }} /> {t("nav.feasibilityStudy")}
      </ToggleButton>
      <ToggleButton value="operations" aria-label={t("nav.operations")}
        disabled={Boolean(monitoringDisabledMessage && demoMarketOperationsDisabledMessage)}>
        <TrendingUpOutlinedIcon sx={{ fontSize: 17 }} /> {t("nav.operations")}
      </ToggleButton>
    </ToggleButtonGroup>
  );

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Sits under the fixed AppBar; keeps the list clear of it. */}
      <Box sx={{ p: 2, fontWeight: 700, fontSize: 16, color: "primary.main" }}>
        {t("nav.menu")}
      </Box>
      <Divider />
      <List
        sx={{
          pt: 1.5,
          borderRadius: 2,
          border: (t) => `1px solid ${t.palette.mode === "dark" ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.08)"}`,
          backgroundColor: (t) =>
            t.palette.mode === "dark" ? "rgba(18,26,22,0.6)" : "rgba(255,255,255,0.95)",
          boxShadow: "0 18px 30px rgba(15,23,42,0.08)",
        }}
      >
        {/* Project: the main page and the asset specifications */}
        <Box sx={{ px: 2, pb: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
          <FolderOpenOutlinedIcon sx={{ color: (theme) => theme.palette.mode === "dark" ? theme.palette.text.secondary : "#5c7792" }} />
          <Typography sx={{ fontWeight: 600, fontSize: 15, lineHeight: 1.3, color: (theme) => theme.palette.mode === "dark" ? theme.palette.text.primary : "#36516b" }}>{t("nav.project")}</Typography>
        </Box>
        {renderNavItem({ label: "Overview & Assets", to: "/project/overview", icon: <AssignmentOutlinedIcon /> })}
        {renderNavItem({ label: "Asset Setup", to: "/project/asset", icon: <TaskOutlinedIcon /> })}

        <Divider sx={{ mx: 1, mt: 0.75, mb: 0 }} />

        {workspace === "operations" ? operationsWorkspace : feasibilityWorkspace}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <Divider sx={{ my: 0.75, borderColor: "divider" }} />
      <Box sx={{ px: 2, pb: 1.25 }}>
        <Typography
          component={Link}
          to="/open-source-notices"
          onClick={() => setAccountDlg(false)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: 11,
            fontWeight: 600,
            color: "primary.main",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 14 }} />
          {t("nav.thirdPartyNotices")}
        </Typography>
      </Box>
    </Box>
  );


  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          backgroundImage: (t) =>
            t.palette.mode === "dark"
              ? (IS_STAGING
                  ? "linear-gradient(270deg, rgba(30,41,59,1) 0%, rgba(51,65,85,0.98) 50%, rgba(71,85,105,0.98) 100%)"
                  : "linear-gradient(270deg, rgba(11,15,20,1) 0%, rgba(13,20,28,0.98) 50%, rgba(10,18,24,0.98) 100%)")
              : (IS_STAGING
                  ? "linear-gradient(0deg, #bbe8c4 0%, #bbe8c4 100%)"
                  : "linear-gradient(270deg, rgba(224, 242, 254, 1) 0%, rgba(149, 180, 255, 0.85) 45%, rgba(5, 139, 157, 0.9) 90%)"),
          borderBottom: "1px solid",
          borderColor: "divider",
          color: (t) => (t.palette.mode === "dark" ? t.palette.text.primary : "#0f172a"),
          boxShadow: (t) =>
            t.palette.mode === "dark"
              ? "0 10px 25px rgba(0,0,0,0.35)"
              : "0 10px 25px rgba(15, 23, 42, 0.15)",
        }}
      >
        <Toolbar sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {/* Mobile menu button */}
            <IconButton
              color="inherit"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 1, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          {/* Left: logo + brand */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.8,
              mr: 0.1,
            }}
          >
            <Box
              component={Link}
              to="/project/overview"
              onClick={() => setAccountDlg(false)}
              aria-label={t("nav.overviewAssets")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.9,
                px: 1.05,
                py: 0.3,
                borderRadius: 1.5,
                backgroundImage:
                  "linear-gradient(145deg, rgba(29, 78, 216, 0.92), rgba(8, 145, 178, 0.88), rgba(125, 211, 252, 0.82))",
                boxShadow:
                  "0 4px 12px rgba(10, 97, 172, 0.4), 0 12px 24px rgba(15,23,42,0.25)",
                border: "1px solid rgba(255,255,255,0.35)",
                minHeight: 42,
                minWidth: 168,
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
                color: "inherit",
                textDecoration: "none",
                cursor: "pointer",
                "&:hover": { filter: "brightness(1.08)" },
                "&:focus-visible": { outline: "2px solid #fff", outlineOffset: 2 },
              }}
            >
              <Avatar
                src="/logo.png"
                alt="MetEn Logo"
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: "#0ea5e9",
                  boxShadow: "0 6px 18px rgba(14,165,233,0.5)",
                  border: "2px solid rgba(255,255,255,0.9)",
                  position: "relative",
                  zIndex: 1,
                }}
              />
              <Typography
                data-no-translate
                sx={{
                  fontWeight: 900,
                  letterSpacing: "0.28em",
                  fontSize: 17,
                  color: "#fff",
                  fontFamily: BRAND_FONT_FAMILY,
                  textShadow: "0 2px 6px rgba(15,23,42,0.6)",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {APP_NAME}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                // Sits low in the row, aligned with the bottom of the brand
                // card rather than centred against it.
                justifyContent: "flex-end",
                minHeight: 42,
                pb: 0.35,
                gap: 0.3,
                ml: 0.2,
              }}
            >
              {IS_STAGING && (
                <Chip
                  label={t("app.test")}
                  sx={{
                    height: 22,
                    fontWeight: 900,
                    letterSpacing: "0.14em",
                    fontSize: 12,
                    borderRadius: 1,
                    bgcolor: "#fbbf24",
                    color: "#78350f",
                    border: "1.5px solid #d97706",
                    boxShadow: "0 2px 6px rgba(217,119,6,0.35)",
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              )}
              <Typography
                sx={{
                  fontSize: 12.5,
                  lineHeight: 1,
                  fontWeight: 700,
                  letterSpacing: "0.03em",
                  color: "rgba(255,255,255,0.95)",
                  textShadow: "0 1px 3px rgba(15,23,42,0.45)",
                  whiteSpace: "nowrap",
                }}
              >
                {t("app.version")}: {versionNode}
              </Typography>
            </Box>
          </Box>

          {/* Top Menus */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.4,
            }}
          >
            <Button onClick={(e) => setFileEl(e.currentTarget)} sx={topMenuBtnSx}>
              {t("menu.file")}
            </Button>

            <Button onClick={(e) => setEditEl(e.currentTarget)} sx={topMenuBtnSx}>
              {t("menu.edit")}
            </Button>

            <Button onClick={(e) => setViewEl(e.currentTarget)} sx={topMenuBtnSx}>
              {t("menu.view")}
            </Button>

            <Button onClick={(e) => setHelpEl(e.currentTarget)} sx={topMenuBtnSx}>
              {t("menu.help")}
            </Button>
          </Box>


          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: 0.4,
            }}
          >
            <Box sx={projectBadgeSx}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 900,
                  letterSpacing: "0.18em",
                  fontSize: 13,
                  color: (t) => (t.palette.mode === "dark" ? "#e0e7ff" : "#1e293b"),
                  textTransform: "none",
                  fontFamily: `"Space Grotesk", "Inter", "Segoe UI", sans-serif`,
                }}
                component="span"
              >
                {t("app.project")}
              </Typography>
              <Typography
                data-no-translate
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  fontSize: 13,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: (t) => (t.palette.mode === "dark" ? "#fff" : "#0f172a"),
                  fontFamily: `"Space Grotesk", "Inter", "Segoe UI", sans-serif`,
                }}
                noWrap
              >
                {title}
              </Typography>
            </Box>
            {workspaceSwitcher}
          </Box>
          <Box sx={{ flexGrow: 1 }} />

          <LanguageSwitcher
            sx={{
              mr: 1,
              flexShrink: 0,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(255,255,255,0.42)",
            }}
          />

          {/* Right: Account menu */}
          {isDemoUser ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                mr: 1,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              <Chip
                label="DEMO"
                size="small"
                color="warning"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  borderRadius: 1,
                  minWidth: 64,
                  height: 28,
                  "& .MuiChip-label": {
                    px: 1.25,
                  },
                }}
              />
              <Chip
                label={t("app.daysLeft", { count: demoProfile?.demoRemainingDays ?? 0 })}
                size="small"
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  backgroundColor: "rgba(255,255,255,0.35)",
                  borderColor: "rgba(255,255,255,0.75)",
                  borderRadius: 1,
                  height: 28,
                  "& .MuiChip-label": {
                    px: 1.25,
                  },
                }}
              />
            </Box>
          ) : null}
          <AnnouncementsBell />
          <IconButton
            color="inherit"
            onClick={(e) => setAccountEl(e.currentTarget)}
            sx={{
              borderRadius: "50%",
              p: 0,
              width: 50,
              height: 50,
              backgroundColor: "rgba(255,255,255,0.25)",
              border: "2px solid rgba(255,255,255,0.75)",
              boxShadow: (t) =>
                t.palette.mode === "dark"
                  ? "0 4px 16px rgba(0,0,0,0.3)"
                  : "0 4px 12px rgba(15,23,42,0.25)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.45)",
              },
            }}
          >
            <Avatar
              sx={{
                width: 44,
                height: 44,
                bgcolor: "background.default",
                border: "1px solid rgba(255,255,255,0.5)",
                fontSize: 20,
                color: "#0B4DA2",
              }}
              src={accountAvatarSrc}
            >
              {avatarInitials}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      <input
        ref={projectJsonInputRef}
        type="file"
        accept={isAdminUser ? `${PROJECT_PACKAGE_EXTENSION},.json,application/json` : PROJECT_PACKAGE_EXTENSION}
        hidden
        onChange={handleProjectPackageFileChange}
      />

      {/* FILE MENU */}
      <Menu
        anchorEl={fileEl}
        open={Boolean(fileEl)}
        onClose={() => setFileEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ onClick: () => setFileEl(null) }}
        slotProps={{ paper: { sx: menuPaperSx } }}
      >
        <MenuItem
          sx={menuItemSx}
          onClick={async () => {
            if (warnProjectCreateBlocked()) return;
            setNewStudyDlg(true);
          }}
        >
          <ListItemIcon sx={menuIconSx}>
            <NoteAddOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("action.new")}
            primaryTypographyProps={{ fontWeight: 700 }}
          />
        </MenuItem>

        <MenuItem
          sx={menuItemSx}
          onClick={async () => {
            setProjects([]);
            setSelProjectId("");
            setProjectsLoading(true);
            setOpenDlg(true);
            try {
              const ps = await metenApi.listProjectSummaries();
              setProjects(ps);
            } catch (err) {
              setOpenDlg(false);
              showSnack({
                message: projectErrorMessage(err, isGreek),
                severity: "error",
                icon: <ErrorOutlineOutlinedIcon fontSize="small" />,
              });
            } finally {
              setProjectsLoading(false);
            }
          }}
        >
          <ListItemIcon sx={menuIconSx}>
            <FolderOpenOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("action.open")}
            primaryTypographyProps={{ fontWeight: 700 }}
          />
        </MenuItem>

        <MenuItem
          sx={menuItemSx}
          onClick={() => {
            projectJsonInputRef.current?.click();
          }}
        >
          <ListItemIcon sx={menuIconSx}>
            <UploadFileOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("action.importProject")}
            primaryTypographyProps={{ fontWeight: 700 }}
          />
        </MenuItem>

        <Divider sx={{ my: 0.75, opacity: 0.6 }} />

        <MenuItem
          disabled={!file.currentProject?.id || !file.isDirty || file.isSaving || saving}
          sx={{
            ...menuItemSx,
            opacity: (!file.currentProject?.id || !file.isDirty) ? 0.5 : 1,
          }}
          onClick={async () => {
            const snapshot = history.present ?? {};
            const { hasMissing } = validateTransformerConfig(snapshot);
            if (hasMissing) {
              try { window.dispatchEvent(new Event(TRANSFORMER_EVENT)); } catch {}
              return;
            }

            setSaving(true);
            try {
              await file.save();
              showSnack({
                message: t("file.saved"),
                severity: "success",
                icon: <SaveOutlinedIcon fontSize="small" />,
              });
            } catch (e) {
              showSnack({
                message: projectErrorMessage(e, isGreek),
                severity: "error",
                icon: <ErrorOutlineOutlinedIcon fontSize="small" />,
              });
            } finally {
              setSaving(false);
            }
          }}
        >
          <ListItemIcon sx={menuIconSx}>
            <SaveOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("action.save")}
            secondary={!file.currentProject?.id ? t("file.useSaveAs") : (!file.isDirty ? t("file.noChanges") : null)}
            secondaryTypographyProps={{ sx: { opacity: 0.75 } }}
            primaryTypographyProps={{ fontWeight: 700 }}
          />
        </MenuItem>

        <MenuItem
          disabled={file.isSaving || saving}
          sx={menuItemSx}
          onClick={() => {
            if (warnProjectCreateBlocked()) return;
            setNewProjectName(file.currentProject?.name || "");
            setSaveAsDlg(true);
          }}
        >
          <ListItemIcon sx={menuIconSx}>
            <SaveAsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("action.saveAs")}
            primaryTypographyProps={{ fontWeight: 700 }}
          />
        </MenuItem>

        <MenuItem
          sx={menuItemSx}
          onClick={async () => {
            const project = file.currentProject;
            if (!project) {
              showSnack({
                message: "No project is currently loaded.",
                severity: "error",
                icon: <ErrorOutlineOutlinedIcon fontSize="small" />,
              });
              return;
            }

            try {
              const config = history.present ?? project.config ?? {};
              const payload = buildProjectExportPayload(project, config);
              const fallbackName = buildProjectPackageFilename(project.name);
              const { blob, filename } = await metenApi.exportProjectPackage(payload, fallbackName);

              downloadBlob(filename || fallbackName, blob);

              showSnack({
                message: "Project exported.",
                severity: "success",
                icon: <FileDownloadOutlinedIcon fontSize="small" />,
              });
            } catch (e) {
              showSnack({
                message: projectErrorMessage(e, isGreek),
                severity: "error",
                icon: <ErrorOutlineOutlinedIcon fontSize="small" />,
              });
            }
          }}
        >
          <ListItemIcon sx={menuIconSx}>
            <FileDownloadOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("action.exportProject")}
            primaryTypographyProps={{ fontWeight: 700 }}
          />
        </MenuItem>

        {isAdminUser ? (
          <MenuItem
            sx={menuItemSx}
            onClick={() => {
              const project = file.currentProject;
              if (!project) {
                showSnack({
                  message: "No project is currently loaded.",
                  severity: "error",
                  icon: <ErrorOutlineOutlinedIcon fontSize="small" />,
                });
                return;
              }

              try {
                const config = history.present ?? project.config ?? {};
                const payload = buildProjectExportPayload(project, config);
                const filename = buildProjectExportFilename(project.name);

                downloadJson(filename, payload);

                showSnack({
                  message: "Project JSON exported (admin).",
                  severity: "success",
                  icon: <FileDownloadOutlinedIcon fontSize="small" />,
                });
              } catch (e) {
                showSnack({
                  message: projectErrorMessage(e, isGreek),
                  severity: "error",
                  icon: <ErrorOutlineOutlinedIcon fontSize="small" />,
                });
              }
            }}
          >
            <ListItemIcon sx={menuIconSx}>
              <FileDownloadOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("action.exportProjectJson")}
              secondary={t("file.adminOnly")}
              secondaryTypographyProps={{ sx: { opacity: 0.75 } }}
              primaryTypographyProps={{ fontWeight: 700 }}
            />
          </MenuItem>
        ) : null}

        <MenuItem
          disabled={!file.currentProject?.id || saving}
          sx={{
            ...menuItemSx,
            opacity: (!file.currentProject?.id || saving) ? 0.5 : 1,
          }}
          onClick={async () => {
            const cur = file.currentProject;
            if (!cur?.id) return;

            if (history.isDirty || file.isDirty) {
              const ok = window.confirm(t("file.deleteUnsaved"));
              if (!ok) return;
            }

            const ok = window.confirm(
              t("file.confirmDelete", { name: cur.name || t("file.untitled") })
            );
            if (!ok) return;

            await handleDeleteProjectFromOpen(cur.id);
          }}
        >
          <ListItemIcon sx={menuIconSx}>
            <DeleteOutlineOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("action.deleteProject")}
            secondary={!file.currentProject?.id ? t("file.noProject") : null}
            secondaryTypographyProps={{ sx: { opacity: 0.75 } }}
            primaryTypographyProps={{ fontWeight: 700 }}
          />
        </MenuItem>


      </Menu>

      {/* EDIT MENU */}
      <Menu
        anchorEl={editEl}
        open={Boolean(editEl)}
        onClose={() => setEditEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ onClick: () => setEditEl(null) }}
        slotProps={{ paper: { sx: menuPaperSx } }}
      >
        <MenuItem
          disabled={!history.canUndo}
          sx={{ ...menuItemSx, opacity: history.canUndo ? 1 : 0.5 }}
          onClick={() => history.undo()}
        >
          <ListItemIcon sx={menuIconSx}>
            <UndoOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("action.undo")} primaryTypographyProps={{ fontWeight: 700 }} />
        </MenuItem>

        <MenuItem
          disabled={!history.canRedo}
          sx={{ ...menuItemSx, opacity: history.canRedo ? 1 : 0.5 }}
          onClick={() => history.redo()}
        >
          <ListItemIcon sx={menuIconSx}>
            <RedoOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("action.redo")} primaryTypographyProps={{ fontWeight: 700 }} />
        </MenuItem>

        <Divider sx={{ my: 0.75, opacity: 0.6 }} />

      </Menu>

      <Menu
        anchorEl={viewEl}
        open={Boolean(viewEl)}
        onClose={() => setViewEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: menuPaperSx } }}
      >
        {/* header */}

        {/* Dark mode */}
        <MenuItem
          sx={menuItemSx}
          onClick={() => setDarkMode((v) => !v)}
        >
          <ListItemIcon sx={menuIconSx}>
            <DarkModeOutlinedIcon fontSize="small" />
          </ListItemIcon>

          <ListItemText
            primary={t("view.darkMode")}
            primaryTypographyProps={{ fontWeight: 700 }}
            secondaryTypographyProps={{ sx: { opacity: 0.75 } }}
          />

          <Switch
            edge="end"
            checked={darkMode}
            onChange={(e) => setDarkMode(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
        </MenuItem>

        {/* Advanced */}
        <MenuItem
          sx={menuItemSx}
          onClick={() => setShowAdvanced((v) => !v)}
        >
          <ListItemIcon sx={menuIconSx}>
            <TuneOutlinedIcon fontSize="small" />
          </ListItemIcon>

          <ListItemText
            primary={t("view.advancedOptions")}
            primaryTypographyProps={{ fontWeight: 700 }}
            secondaryTypographyProps={{ sx: { opacity: 0.75 } }}
          />

          <Switch
            edge="end"
            checked={showAdvanced}
            onChange={(e) => setShowAdvanced(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
        </MenuItem>

        {/* Tooltips */}
        <MenuItem
          sx={menuItemSx}
          onClick={() => setShowTooltips((v) => !v)}
        >
          <ListItemIcon sx={menuIconSx}>
            <TipsAndUpdatesOutlinedIcon fontSize="small" />
          </ListItemIcon>

          <ListItemText
            primary={t("view.tooltips")}
            primaryTypographyProps={{ fontWeight: 700 }}
            secondaryTypographyProps={{ sx: { opacity: 0.75 } }}
          />

          <Switch
            edge="end"
            checked={showTooltips}
            onChange={(e) => setShowTooltips(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
        </MenuItem>
      </Menu>

      {/* HELP MENU */}
      <Menu
        anchorEl={helpEl}
        open={Boolean(helpEl)}
        onClose={() => setHelpEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ onClick: () => setHelpEl(null) }}
        slotProps={{ paper: { sx: menuPaperSx } }}
      >
        <MenuItem
          sx={menuItemSx}
          onClick={() => window.open(DOCS_URL, "_blank", "noopener,noreferrer")}
        >
          <ListItemIcon sx={menuIconSx}>
            <DescriptionOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("help.documentation")}
            primaryTypographyProps={{ fontWeight: 600 }}
          />
        </MenuItem>

        <MenuItem
          sx={menuItemSx}
          data-testid="help-manual-btm"
          onClick={() => window.open(MANUAL_URLS.btm, "_blank", "noopener,noreferrer")}
        >
          <ListItemIcon sx={menuIconSx}>
            <PictureAsPdfOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("help.manualBtm")} primaryTypographyProps={{ fontWeight: 600 }} />
        </MenuItem>

        <MenuItem
          sx={menuItemSx}
          data-testid="help-manual-merchant"
          onClick={() => window.open(MANUAL_URLS.merchant, "_blank", "noopener,noreferrer")}
        >
          <ListItemIcon sx={menuIconSx}>
            <PictureAsPdfOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("help.manualMerchant")} primaryTypographyProps={{ fontWeight: 600 }} />
        </MenuItem>

        <MenuItem sx={menuItemSx} onClick={() => setGlossaryDlg(true)}>
          <ListItemIcon sx={menuIconSx}>
            <MenuBookOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("help.glossary")} primaryTypographyProps={{ fontWeight: 600 }} />
        </MenuItem>

        <MenuItem sx={menuItemSx} onClick={() => setShortcutsDlg(true)}>
          <ListItemIcon sx={menuIconSx}>
            <KeyboardOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("help.keyboardShortcuts")}
            primaryTypographyProps={{ fontWeight: 600 }}
          />
        </MenuItem>

        <Divider sx={{ my: 0.75, opacity: 0.6 }} />

        <MenuItem sx={menuItemSx} onClick={() => setReportDlg(true)}>
          <ListItemIcon sx={menuIconSx}>
            <BugReportOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("help.reportBug")}
            primaryTypographyProps={{ fontWeight: 600 }}
          />
        </MenuItem>

        <MenuItem
          sx={menuItemSx}
          onClick={() =>
            window.open(`${WEBSITE_URL}/contact`, "_blank", "noopener,noreferrer")
          }
        >
          <ListItemIcon sx={menuIconSx}>
            <SupportAgentOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t("help.contactSupport")} primaryTypographyProps={{ fontWeight: 600 }} />
        </MenuItem>

        <Divider sx={{ my: 0.75, opacity: 0.6 }} />

        <MenuItem
          sx={{
            ...menuItemSx,
            backgroundColor: (t) =>
              t.palette.mode === "dark"
                ? "rgba(255,255,255,0.03)"
                : "rgba(0,0,0,0.02)",
          }}
          onClick={() => setAboutDlg(true)}
        >
          <ListItemIcon sx={menuIconSx}>
            <InfoOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("help.about", { app: APP_NAME })}
            primaryTypographyProps={{ fontWeight: 800 }}
          />
        </MenuItem>
      </Menu>

      {/* ACCOUNT MENU */}
      <Menu
        anchorEl={accountEl}
        open={Boolean(accountEl)}
        onClose={() => setAccountEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiMenuItem-root": { color: "text.primary" },
          "& .MuiMenuItem-root.Mui-disabled": { color: "text.secondary", opacity: 1 },
        }}
      >
        <MenuItem
          disabled
          sx={{
            justifyContent: "flex-start",
            opacity: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
            {t("account.signedInAs")}&nbsp;
            <Box data-no-translate component="span" sx={{ fontWeight: 800, color: "text.primary" }}>
              {accountDisplayName}
            </Box>
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setAccountEl(null);
            setAccountDlg(true);
          }}
        >
          {t("account.account")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAccountEl(null);
            logout();
          }}
        >
          {t("account.logout")}
        </MenuItem>
      </Menu>

      {!isAdminUsersRoute && (
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            position: "fixed",
            top: 56,
            bottom: 0,
            left: sidebarCollapsed ? 0 : visibleDrawerWidth - 12,
            width: 12,
            zIndex: (theme) => theme.zIndex.drawer + 2,
            transition: isResizingSidebar ? "none" : "left 0.15s ease",
            // Share the hover region so moving from the edge onto the arrow
            // never hides the control. Keyboard and touch keep it accessible.
            "&:hover .sidebar-toggle, &:has(:focus-visible) .sidebar-toggle": {
              opacity: 1, pointerEvents: "auto",
            },
            "@media (hover: none)": {
              "& .sidebar-toggle": { opacity: 1, pointerEvents: "auto" },
            },
          }}
        >
          {/* The full-height edge stays available while the page scrolls. */}
          {!sidebarCollapsed && (
            <Box
              role="separator"
              aria-orientation="vertical"
              aria-label={isGreek ? "Αλλαγή πλάτους μενού" : "Resize sidebar"}
              aria-valuemin={DRAWER_WIDTH_MIN}
              aria-valuemax={drawerWidthForViewport(DRAWER_WIDTH_MAX, viewportWidth)}
              aria-valuenow={visibleDrawerWidth}
              title={isGreek ? "Σύρετε για αλλαγή πλάτους · διπλό κλικ για επαναφορά" : "Drag to resize · double-click to reset"}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Home") return;
                e.preventDefault();
                if (e.key === "Home") {
                  resetDrawerWidth();
                  return;
                }
                setDrawerWidth((width) => {
                  const next = drawerWidthForViewport(width + (e.key === "ArrowRight" ? 16 : -16), window.innerWidth);
                  try {
                    window.localStorage.setItem(DRAWER_WIDTH_KEY, String(next));
                  } catch {
                    /* storage unavailable */
                  }
                  return next;
                });
              }}
              onMouseDown={(e) => {
                if (e.button !== 0) return;
                e.preventDefault();
                setIsResizingSidebar(true);
              }}
              onDoubleClick={resetDrawerWidth}
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                width: 12,
                cursor: "col-resize",
                zIndex: 2,
                // A wide grab area, but only a thin line is drawn at the drawer's edge.
                "&::after": {
                  content: '""', position: "absolute", top: 0, bottom: 0, right: 0, width: 2,
                  backgroundColor: "primary.main",
                  opacity: isResizingSidebar ? 0.7 : 0,
                  transition: "opacity 0.15s ease",
                },
                "&:hover::after": { opacity: 0.5 },
              }}
            />
          )}
          <IconButton
            className="sidebar-toggle"
            onClick={toggleSidebar}
            onMouseDown={(event) => event.stopPropagation()}
            aria-label={sidebarCollapsed
              ? (isGreek ? "Εμφάνιση μενού" : "Show sidebar")
              : (isGreek ? "Απόκρυψη μενού" : "Hide sidebar")}
            aria-controls="desktop-sidebar"
            aria-expanded={!sidebarCollapsed}
            title={sidebarCollapsed
              ? (isGreek ? "Εμφάνιση μενού" : "Show sidebar")
              : (isGreek ? "Απόκρυψη μενού" : "Hide sidebar")}
            sx={{
              display: { xs: "none", md: "inline-flex" },
              position: "absolute",
              left: sidebarCollapsed ? 13 : 12,
              bottom: 64,
              transform: "translateX(-50%)",
              width: 26,
              height: 30,
              borderRadius: 1,
              zIndex: (theme) => theme.zIndex.drawer + 2,
              color: "primary.main",
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              boxShadow: 1,
              opacity: sidebarCollapsed ? 1 : 0,
              pointerEvents: sidebarCollapsed ? "auto" : "none",
              transition: "opacity 0.15s ease",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            {sidebarCollapsed ? <ChevronRightIcon sx={{ fontSize: 18 }} /> : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>
      )}

      {!isAdminUsersRoute ? (
      <Box component="nav" sx={{ width: { md: visibleDrawerWidth }, flexShrink: { md: 0 }, transition: isResizingSidebar ? "none" : "width 0.15s ease" }}>
        {/* Mobile temporary drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: `min(${drawerWidth}px, calc(100vw - 24px))`,

              bgcolor: (t) => (t.palette.mode === "dark" ? "#0D141C" : "#F2FBF6"),
              color: (t) => (t.palette.mode === "dark" ? "#EAF7EE" : "#102A43"),
              borderRight: (t) =>
                t.palette.mode === "dark"
                  ? "1px solid rgba(255,255,255,0.08)"
                  : "1px solid rgba(0,0,0,0.08)",
              backgroundImage: "none",
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop permanent drawer — width is user-adjustable, see the handle below */}
        <Drawer
          variant="permanent"
          id="desktop-sidebar"
          sx={{
            display: { xs: "none", md: sidebarCollapsed ? "none" : "block" },
            "& .MuiDrawer-paper": {
              width: visibleDrawerWidth,
              boxSizing: "border-box",
              // No animation while dragging: a width transition makes the edge
              // lag behind the cursor and feel broken. Kept for programmatic
              // changes such as the double-click reset.
              transition: isResizingSidebar ? "none" : "width 0.15s ease",
              overflowX: "hidden",

              bgcolor: (t) => (t.palette.mode === "dark" ? "#0D141C" : "#f8fbfaff"),
              color: (t) => (t.palette.mode === "dark" ? "#EAF7EE" : "#102A43"),
              borderRight: (t) =>
                t.palette.mode === "dark"
                  ? "1px solid rgba(255,255,255,0.08)"
                  : "1px solid rgba(0,0,0,0.08)",
              backgroundImage: "none",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      ) : null}

      {/* Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: isAdminUsersRoute ? "100%" : { md: `calc(100% - ${visibleDrawerWidth}px)` },
            mt: accountDlg || effectiveMustSetPassword || isPasswordGatePending ? 6 : 7,
            p: isAdminUsersRoute ? 0 : { xs: 1, md: 1.5 },
            backgroundColor: "background.default",
            minHeight: "100vh",
          }}
        >
        {isPasswordGatePending ? (
          <Box
            component="section"
            sx={{
              width: "100%",
              minHeight: "calc(100vh - 88px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: { xs: 1, md: 3 },
              py: { xs: 2, md: 4 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={96} thickness={4} sx={{ color: "#0b3b5e" }} />
            </Box>
          </Box>
        ) : effectiveMustSetPassword ? (
          <Box
            component="section"
            sx={{
              width: "100%",
              minHeight: "calc(100vh - 88px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: { xs: 1, md: 3 },
              py: { xs: 2, md: 4 },
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 560,
                backgroundColor: "background.paper",
                borderRadius: 3,
                p: { xs: 2.5, md: 4 },
                boxShadow: (t) =>
                  `0 25px 45px rgba(15,23,42, ${t.palette.mode === "dark" ? 0.45 : 0.1})`,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: "0.02em" }}>
                Set your password
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You must change your password before continuing.
              </Typography>
              <TextField
                label="Email"
                value={user?.email || "-"}
                fullWidth
                disabled
                sx={readOnlyFieldSx}
                InputLabelProps={{ sx: accountFieldLabelSx }}
              />
              <TextField
                label="New password"
                type={passwordVisibility.new ? "text" : "password"}
                value={passwordSetupState.newPassword}
                onChange={(e) =>
                  setPasswordSetupState((prev) => ({
                    ...prev,
                    newPassword: e.target.value,
                    message: "",
                  }))
                }
                fullWidth
                disabled={passwordSetupState.loading}
                sx={{ ...accountTextFieldSx, ...passwordInputSx }}
                InputLabelProps={{ sx: accountFieldLabelSx }}
                InputProps={{
                  endAdornment: passwordAdornment("new"),
                }}
              />
              <TextField
                label="Confirm password"
                type={passwordVisibility.confirm ? "text" : "password"}
                value={passwordSetupState.confirmPassword}
                onChange={(e) =>
                  setPasswordSetupState((prev) => ({
                    ...prev,
                    confirmPassword: e.target.value,
                    message: "",
                  }))
                }
                fullWidth
                disabled={passwordSetupState.loading}
                sx={{ ...accountTextFieldSx, ...passwordInputSx }}
                InputLabelProps={{ sx: accountFieldLabelSx }}
                InputProps={{
                  endAdornment: passwordAdornment("confirm"),
                }}
              />
              {passwordSetupMismatch && (
                <Typography variant="caption" color="error">
                  New passwords must match
                </Typography>
              )}
              {passwordSetupState.message && (
                <Alert severity={passwordSetupState.severity}>
                  {passwordSetupState.message}
                </Alert>
              )}
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", pt: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleInitialPasswordSetup}
                  disabled={passwordSetupState.loading || !isPasswordSetupValid}
                >
                  {passwordSetupState.loading ? "Saving..." : "Create password"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={logout}
                  disabled={passwordSetupState.loading}
                >
                  Logout
                </Button>
              </Box>
            </Box>
          </Box>
        ) : accountDlg ? (
          <Box
            component="section"
            data-dirty-ignore="true"
          sx={{
            width: "100%",
            minHeight: "calc(100vh - 88px)",
            backgroundColor: "background.default",
            px: { xs: 1, md: 1 },
            py: { xs: 1, md: 1 },
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                letterSpacing: "0.02em",
              }}
            >
              Account Profile
            </Typography>
            {isAdminUser ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: -0.25 }}>
                <Chip
                  label="ADMIN"
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 800, letterSpacing: "0.08em" }}
                />
                <Typography variant="caption" color="text.secondary">
                  This profile has administrator access.
                </Typography>
              </Box>
            ) : null}
            <Box
              sx={{
                width: "100%",
                backgroundColor: "background.paper",
                borderRadius: 2,
                p: { xs: 2, md: 3 },
                boxShadow: (t) =>
                  `0 25px 45px rgba(15,23,42, ${t.palette.mode === "dark" ? 0.45 : 0.1})`,
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
              }}
            >
              <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <Avatar sx={{ width: 64, height: 64 }} src={avatarPreview || accountAvatarSrc}>
                  {avatarInitials}
                </Avatar>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  <Typography variant="subtitle2" sx={accountFieldTitleSx}>
                    Profile picture
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flex: "1 1 auto",
                      gap: 1,
                      flexWrap: "wrap",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Button component="label" variant="outlined" size="small">
                        Upload
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={handleAvatarSelect}
                        />
                      </Button>
                      <Button size="small" variant="outlined" onClick={openPresetMenu}>
                        Choose
                      </Button>
                    </Box>
                    {(avatarPreview || profileAvatarUrl) && (
                      <Box sx={{ ml: "auto" }}>
                        <Button size="small" onClick={handleClearAvatar}>
                          Remove
                        </Button>
                      </Box>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {avatarFileName || "PNG / JPG / GIF (max 4MB)"}
                  </Typography>
                </Box>
              </Box>
              <Menu
                anchorEl={presetAnchor}
                open={Boolean(presetAnchor)}
                onClose={closePresetMenu}
                MenuListProps={{ sx: { p: 1 } }}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                {PRESET_AVATARS.map((preset) => (
                  <MenuItem
                    key={preset.url}
                    onClick={() => handlePresetSelect(preset)}
                    sx={{ justifyContent: "center", px: 0.75 }}
                  >
                    <Avatar src={preset.url} sx={{ width: 36, height: 36 }} />
                  </MenuItem>
                ))}
              </Menu>

                <TextField
                  label="Display name"
                  value={displayNameInput || profileDisplayName || fallbackDisplayName || ""}
                  placeholder={fallbackDisplayName || "Enter a display name"}
                  onChange={(e) => setDisplayNameInput(e.target.value)}
                  fullWidth
                  disabled={profileLoading || profileSaving}
                  sx={accountTextFieldSx}
                  InputLabelProps={{ sx: accountFieldLabelSx }}
                />

              <Box
                sx={{
                  display: "grid",
                  gap: 1,
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                }}
              >
                <TextField
                  label="First name"
                  value={firstNameTrimmed || nameTokens[0] || ""}
                  placeholder="-"
                  InputProps={{
                    readOnly: true,
                    inputProps: {
                      style: {
                        caretColor: "transparent",
                        cursor: "default",
                        userSelect: "text",
                      },
                    },
                  }}
                  fullWidth
                  sx={readOnlyFieldSx}
                  InputLabelProps={{ sx: accountFieldLabelSx }}
                />
                <TextField
                  label="Last name"
                  value={lastNameTrimmed || nameTokens.slice(1).join(" ") || ""}
                  placeholder="-"
                  InputProps={{
                    readOnly: true,
                    inputProps: {
                      style: {
                        caretColor: "transparent",
                        cursor: "default",
                        userSelect: "text",
                      },
                    },
                  }}
                  fullWidth
                  sx={readOnlyFieldSx}
                  InputLabelProps={{ sx: accountFieldLabelSx }}
                />
              </Box>

                <Box
                  sx={{
                    display: "grid",
                    gap: 1,
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  }}
                >
                <TextField
                  label="Role"
                  value={
                    jobRoleInput ||
                    profileJobRole ||
                    ""
                  }
                  onChange={(e) => setJobRoleInput(e.target.value)}
                  placeholder="-"
                  fullWidth
                  disabled={profileLoading || profileSaving}
                  sx={accountTextFieldSx}
                  InputLabelProps={{ sx: accountFieldLabelSx }}
                />
                <TextField
                  label="Organization"
                  value={profileOrganization || ""}
                  placeholder="-"
                  InputProps={{
                    readOnly: true,
                    inputProps: {
                      style: {
                        caretColor: "transparent",
                        cursor: "default",
                        userSelect: "text",
                      },
                    },
                  }}
                  fullWidth
                  sx={readOnlyFieldSx}
                  InputLabelProps={{ sx: accountFieldLabelSx }}
                />
                </Box>

              <TextField
                label="Email"
                value={profile?.email || user?.email || "-"}
                InputProps={{
                  readOnly: true,
                  sx: {
                    color: (t) =>
                      t.palette.mode === "dark" ? "rgba(255,255,255,0.95)" : "#0f172a",
                    fontWeight: 600,
                  },
                }}
                disabled
                fullWidth
                sx={readOnlyFieldSx}
                InputLabelProps={{ sx: accountFieldLabelSx }}
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  gap: 2,
                  flexWrap: "wrap",
                  mt: 2,
                  mb: 1,
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleProfileSave}
                  disabled={profileLoading || profileSaving || !hasProfileChanges}
                >
                  {profileSaving ? "Saving..." : "Submit changes"}
                </Button>
              </Box>

              <AccountUsagePanel />

              {isDemoUser ? (
                <Box
                  sx={{
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    p: 2,
                    backgroundColor: (t) =>
                      t.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#f8fbff",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.25,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "warning.main", letterSpacing: "0.03em" }}>
                      Demo Access
                    </Typography>
                    {demoProfile?.demoLocked ? (
                      <Chip label="Demo locked" color="error" size="small" sx={{ fontWeight: 700 }} />
                    ) : null}
                  </Box>
                  {demoProfile?.demoLocked ? (
                    <Alert severity="error">This demo account is locked.</Alert>
                  ) : null}
                  <Box
                    sx={{
                      display: "grid",
                      gap: 1,
                      gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                    }}
                  >
                    <TextField
                      label="Demo remaining days"
                      value={demoLoading ? "Loading..." : String(demoProfile?.demoRemainingDays ?? 0)}
                      fullWidth
                      InputProps={{ readOnly: true }}
                      sx={readOnlyFieldSx}
                      InputLabelProps={{ sx: accountFieldLabelSx }}
                    />
                    <TextField
                      label="Market optimization limit per day"
                      value={
                        demoLoading
                          ? "Loading..."
                          : String(demoProfile?.demoMarketOptimizationLimitPerDay ?? 0)
                      }
                      fullWidth
                      InputProps={{ readOnly: true }}
                      sx={readOnlyFieldSx}
                      InputLabelProps={{ sx: accountFieldLabelSx }}
                    />
                    <TextField
                      label="Feasibility limit per day"
                      value={
                        demoLoading
                          ? "Loading..."
                          : String(demoProfile?.demoFeasibilityLimitPerDay ?? 0)
                      }
                      fullWidth
                      InputProps={{ readOnly: true }}
                      sx={readOnlyFieldSx}
                      InputLabelProps={{ sx: accountFieldLabelSx }}
                    />
                    <TextField
                      label="Remaining market optimizations today"
                      value={
                        demoLoading
                          ? "Loading..."
                          : String(demoProfile?.marketOptimizationRemainingToday ?? 0)
                      }
                      fullWidth
                      InputProps={{ readOnly: true }}
                      sx={readOnlyFieldSx}
                      InputLabelProps={{ sx: accountFieldLabelSx }}
                    />
                    <TextField
                      label="Remaining feasibility runs today"
                      value={
                        demoLoading
                          ? "Loading..."
                          : String(demoProfile?.feasibilityRemainingToday ?? 0)
                      }
                      fullWidth
                      InputProps={{ readOnly: true }}
                      sx={readOnlyFieldSx}
                      InputLabelProps={{ sx: accountFieldLabelSx }}
                    />
                  </Box>
                </Box>
              ) : null}

              {isAdminUser ? (
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: { xs: "100%", md: 560 },
                    alignSelf: "flex-start",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    p: 2,
                    backgroundColor: (t) =>
                      t.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#f8fbff",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.25,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 800, color: "primary.main", letterSpacing: "0.03em" }}
                      >
                        User Management
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Admin-only access to user details and demo configuration.
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setAccountDlg(false);
                        navigate("/admin/users", {
                          state: {
                            accountReturnTo: `${location.pathname}${location.search}${location.hash}`,
                          },
                        });
                      }}
                    >
                      Manage users
                    </Button>
                  </Box>
                </Box>
              ) : null}

              <Box
                sx={{
                  pt: 2.5,
                  borderTop: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.8,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: (t) => (t.palette.mode === "dark" ? t.palette.text.secondary : brandBlue),
                    fontWeight: 700,
                    letterSpacing: "0.02em",
                    fontSize: { xs: 14, md: 16 },
                  }}
                >
                  Password
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5, mt: -0.6 }}
                >
                  Change your password.
                </Typography>
                {[
                  {
                    label: "Current password",
                    field: "current",
                    stateKey: "currentPassword",
                  },
                  { label: "New password", field: "new", stateKey: "newPassword" },
                  {
                    label: "Confirm password",
                    field: "confirm",
                    stateKey: "confirmPassword",
                  },
                ].map(({ label, field, stateKey }) => (
                  <TextField
                    key={field}
                    label={label}
                    type={passwordVisibility[field] ? "text" : "password"}
                    value={passwordState[stateKey]}
                    onChange={(e) => {
                      setPasswordState((prev) => ({
                        ...prev,
                        [stateKey]: e.target.value,
                      }));
                      setPasswordStatus((prev) => ({ ...prev, message: "" }));
                    }}
                    fullWidth
                    disabled={passwordStatus.loading}
                    sx={{ ...accountTextFieldSx, ...passwordInputSx, mb: 1 }}
                    InputLabelProps={{ sx: accountFieldLabelSx }}
                    InputProps={{
                      endAdornment: passwordAdornment(field),
                    }}
                  />
                ))}
                {passwordMismatch && (
                  <Typography variant="caption" color="error">
                    New passwords must match
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  onClick={handlePasswordChange}
                  disabled={passwordStatus.loading || !isPasswordFormValid}
                >
                  {passwordStatus.loading ? "Updating..." : "Update password"}
                </Button>
                {passwordStatus.message && (
                  <Alert severity={passwordStatus.severity} sx={{ mt: 1 }}>
                    {passwordStatus.message}
                  </Alert>
                )}
              </Box>

            </Box>
          </Box>
        ) : (
          <>
            {maintenanceStatus.maintenance_mode ? (
              <Alert
                severity="warning"
                variant="filled"
                sx={{
                  // Keep the maintenance notice pinned under the header while the
                  // page scrolls, so it stays visible at all times.
                  position: "sticky",
                  // Offset by the fixed AppBar height (MUI Toolbar: 56px xs / 64px sm+)
                  // so the notice pins directly below the header instead of
                  // scrolling underneath it.
                  top: { xs: 56, sm: 64 },
                  zIndex: (t) => t.zIndex.appBar - 1,
                  whiteSpace: "pre-line",
                  mb: 2,
                  borderRadius: 2,
                  boxShadow: "0 6px 16px rgba(15, 23, 42, 0.22)",
                }}
              >
                {maintenanceNotice(isGreek, maintenanceStatus.maintenance_message, isAdminUser)}
              </Alert>
            ) : null}
            <QuickStudyBanner isGreek={isGreek} />
            {hideWeatherPage ? <Navigate to="/dashboard?view=market&marketTab=prices" replace /> : <Outlet />}
          </>
        )}
        {/* ABOUT DIALOG */}
        <Dialog open={aboutDlg} onClose={() => setAboutDlg(false)} maxWidth="sm" fullWidth>
          <DialogTitle>About {APP_NAME}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mt: 1 }}>
              {APP_NAME} — PV & BESS techno-economic optimization and market dispatch platform.
            </Typography>

            <Typography variant="body2" sx={{ mt: 2, opacity: 0.8 }}>
              Version: {versionNode}
            </Typography>

            <Box sx={{ display: "flex", gap: 1.5, mt: 2, flexWrap: "wrap" }}>
              <Button variant="outlined" onClick={() => window.open(WEBSITE_URL, "_blank", "noopener,noreferrer")}>
                Website
              </Button>
              <Button variant="outlined" onClick={() => window.open(`${WEBSITE_URL}/privacy-policy`, "_blank", "noopener,noreferrer")}>
                Privacy
              </Button>
              <Button variant="outlined" onClick={() => window.open(`${WEBSITE_URL}/terms-conditions`, "_blank", "noopener,noreferrer")}>
                Terms
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAboutDlg(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* GLOSSARY DIALOG */}
        <GlossaryDialog open={glossaryDlg} onClose={() => setGlossaryDlg(false)} />

        {/* WHAT STUDY DO YOU WANT? (File > New) */}
        <NewStudyDialog
          open={newStudyDlg}
          isGreek={isGreek}
          openProjectName={file.currentProject?.name || ""}
          onCancel={() => setNewStudyDlg(false)}
          onCreate={createNewStudy}
          onSkip={() => createNewStudy({ kind: "other" })}
        />

        {/* REPORT A PROBLEM */}
        <ReportProblemDialog
          open={reportDlg}
          onClose={() => setReportDlg(false)}
          supportEmail={SUPPORT_EMAIL}
          context={{
            appName: APP_NAME,
            appVersion: APP_VERSION || "",
            page: location.pathname,
            projectId: file.currentProject?.id || null,
            projectName: file.currentProject?.name || "",
            userEmail: profile?.email || user?.email || "",
            browser: typeof navigator !== "undefined" ? navigator.userAgent : "",
            screen: typeof window !== "undefined" ? `${window.innerWidth}×${window.innerHeight}` : "",
          }}
        />

        {/* SHORTCUTS DIALOG */}
        <Dialog open={shortcutsDlg} onClose={() => setShortcutsDlg(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogContent sx={{ pt: 1.5 }}>
            <Typography variant="body2" sx={{ mb: 1.5, opacity: 0.85 }}>
              These work when you are not typing in an input field.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ fontWeight: 700 }}>Undo</Typography>
                <Typography sx={{ opacity: 0.85 }}>Ctrl + Z (Cmd + Z on Mac)</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ fontWeight: 700 }}>Redo</Typography>
                <Typography sx={{ opacity: 0.85 }}>Ctrl + Y / Ctrl + Shift + Z</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ fontWeight: 700 }}>Save</Typography>
                <Typography sx={{ opacity: 0.85 }}>Ctrl + S (Cmd + S on Mac)</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ fontWeight: 700 }}>{t("file.saveAsTitle")}</Typography>
                <Typography sx={{ opacity: 0.85 }}>Ctrl + Shift + S (Cmd + Shift + S on Mac)</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ fontWeight: 700 }}>Open</Typography>
                <Typography sx={{ opacity: 0.85 }}>Ctrl + O (Cmd + O on Mac)</Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShortcutsDlg(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* OPEN DIALOG */}
        <Dialog open={openDlg} onClose={() => setOpenDlg(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Open</DialogTitle>

          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="open-project-label">Project</InputLabel>
              <Select
                labelId="open-project-label"
                label="Project"
                value={selProjectId}
                disabled={projectsLoading}
                onChange={(e) => setSelProjectId(e.target.value)}
                renderValue={(val) => {
                  if (!val) return "";
                  const p = projects.find((x) => x.id === val);
                  return p?.name || "";
                }}
              >
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id} sx={{ pr: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 1 }}>
                      <Typography sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        {p.name}
                      </Typography>

                      <IconButton
                        size="small"
                        disabled={saving}
                        onClick={(ev) => {
                          ev.preventDefault();
                          ev.stopPropagation();
                          const ok = window.confirm(t("file.confirmDelete", { name: p.name || t("file.untitled") }));
                          if (!ok) return;
                          handleDeleteProjectFromOpen(p.id);
                        }}
                        sx={{ opacity: 0.8, "&:hover": { opacity: 1 } }}
                      >
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {projectsLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} data-testid="open-projects-loading">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary" data-no-translate>
                  {isGreek ? "Φόρτωση έργων…" : "Loading projects…"}
                </Typography>
              </Box>
            ) : null}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpenDlg(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!selProjectId}
              onClick={async () => {
                if (history.isDirty || file.isDirty) {
                  const ok = window.confirm(t("file.discard"));
                  if (!ok) return;
                }

                await file.open(selProjectId);

                showSnack({
                  message: t("file.loaded"),
                  severity: "info",
                  icon: <FolderOpenOutlinedIcon fontSize="small" />,
                });

                setOpenDlg(false);
              }}
            >
              Open
            </Button>
          </DialogActions>
        </Dialog>

        {/* SAVE AS DIALOG */}
        <Dialog open={saveAsDlg} onClose={saving ? undefined : () => setSaveAsDlg(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t("file.saveAsTitle")}</DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label={t("file.newName")}
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
            />
            {demoProjectCreateDisabledMessage ? (
              <Alert severity="warning">{demoProjectCreateDisabledMessage}</Alert>
            ) : null}
          </DialogContent>

          <DialogActions>
            <Button disabled={saving} onClick={() => setSaveAsDlg(false)}>
              {t("action.cancel")}
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress color="inherit" size={16} /> : undefined}
              disabled={saving || !newProjectName.trim() || Boolean(demoProjectCreateDisabledMessage)}
              onClick={async () => {
                if (warnProjectCreateBlocked()) return;
                const snapshot = history.present ?? {};
                const { hasMissing } = validateTransformerConfig(snapshot);
                if (hasMissing) {
                  try {
                    window.dispatchEvent(new Event(TRANSFORMER_EVENT));
                  } catch {}
                  return;
                }

                setSaving(true);
                try {
                  const res = await file.saveAs({ name: newProjectName.trim() });
                  setSaveAsDlg(false);

                  showSnack({
                    message: t("file.savedAs", { name: res?.name ?? newProjectName.trim() }),
                    severity: "success",
                    icon: <SaveAsOutlinedIcon fontSize="small" />,
                  });
                } catch (e) {
                  showSnack({
                    message: projectErrorMessage(e, isGreek),
                    severity: "error",
                    icon: <ErrorOutlineOutlinedIcon fontSize="small" />,
                  });
                } finally {
                  setSaving(false);
                }
              }}
            >
              {saving ? t("action.saving") : t("action.save")}
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar open={Boolean(file.isSaving)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
          <Alert role="status" severity="info" variant="filled" icon={<CircularProgress size={18} color="inherit" />}
            sx={{ alignItems: "center", borderRadius: 2, boxShadow: 6 }}>
            {isGreek ? "Αποθήκευση έργου…" : "Saving project…"}
          </Alert>
        </Snackbar>
        {/* ✅ SAVE FEEDBACK */}
        <Snackbar
          open={snack.open && !file.isSaving}
          autoHideDuration={2400}
          onClose={closeSnack}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={closeSnack}
            severity={snack.severity}
            variant="filled"
            icon={snack.icon ?? <CheckCircleOutlineIcon fontSize="small" />}
            sx={{
              alignItems: "center",
              fontWeight: 600,
              fontSize: 13,
              borderRadius: 2,
              boxShadow: 6,
              px: 1.25,
              py: 0.75,
              letterSpacing: "0.01em",
              "& .MuiAlert-icon": { mt: "2px" },
              ...(snack.severity === "error"
                ? { bgcolor: (t) => (t.palette.mode === "dark" ? "#b71c1c" : "#d32f2f") }
                : {}),
            }}
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}
